import React, { useEffect, useState, useRef } from 'react';
import { Button, ConfigProvider, Form, Input, Select, Tabs, InputNumber, message, Modal, Upload, InputNumberProps, Tooltip, Space } from 'antd';
import type { TabsProps, UploadFile } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import EditableDrawer from '../../../../components/EditableDrawer';
import { ChannelType } from '../graphql/types/ChannelType';
import { useTranslation } from 'react-i18next';
import { hasResource } from "../../../../utils/resourceAuth";
import { ResourceCode } from "../../../../constants/resourceCodes";
import { useAllSettlementTrackingPoints } from '../graphql/queries/getAllSettlementTrackingPoints';
import { useAllContractingParties } from '../graphql/queries/getContractingParties';
import { useCompleteChannelInfo } from '../graphql/queries/getCompleteChannelInfo';
import { useSaveChannelExtendedInfo } from '../graphql/mutations/saveChannelExtendedInfo';
import { useSaveChannel, ChannelInput } from '../graphql/mutations/saveChannel';
import { useUpdateChannelPortalConfig } from '../graphql/mutations/updateChannelPortalConfig';
import { useOnboardingDocuments } from '../graphql/queries/getOnboardingDocuments';
import { useUploadOnboardingDocument, useDeleteOnboardingDocument, useEnqueueChannelOnboardingDocumentDownload } from '../graphql/mutations/onboardingDocuments';
import OnboardingDocumentDownloadModal from './OnboardingDocumentDownloadModal';
import { useSendApiKey } from '../graphql/mutations/sendApiKey';
import { fileUpload } from '../../../../utils/storageUtils';
import { DeleteOutlined, DownloadOutlined, QuestionCircleOutlined, UserAddOutlined, FileSearchOutlined } from '@ant-design/icons';
import CustomerAccountModal from './CustomerAccountModal';
import CustomerLogModal from './CustomerLogModal';
import type { RcFile } from 'antd/es/upload';
import { useAllAvailablePickupHubs, useConfiguredPickupHubs } from '../graphql/queries/pickupHubZipcode';
import { useSaveOrUpdatePickupHubConfigs } from '../graphql/mutations/pickupHubZipcode';
import { useValidateZipcodesExist } from '../graphql/queries/validateZipcodesExist';
import { useIsZipcodeInHubAreas } from '../graphql/queries/isZipcodeInHubAreas';
import { GET_ALL_CHANNELS } from '../graphql/queries/getAllChannels';
import { GET_COMPLETE_CHANNEL_INFO } from '../graphql/queries/getCompleteChannelInfo';

interface ChannelFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: ChannelType) => void;
    channel: ChannelType | null;
    mode: 'create' | 'edit';
    /** 仅持有「查看客户资料」而无「编辑客户资料」资源码时为 true：表单禁用、隐藏保存 */
    readOnly?: boolean;
}

const ChannelFormDrawer: React.FC<ChannelFormDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    channel,
    mode,
    readOnly = false
}) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const [activeTab, setActiveTab] = useState('basic');
    const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(['basic'])); // 跟踪用户访问过的标签页
    const [confirmationChars, setConfirmationChars] = useState(['', '', '', '']);
    const [confirmationInputs, setConfirmationInputs] = useState(['', '', '', '']); // 临时输入值
    const [sendKeyDisabled, setSendKeyDisabled] = useState(true);
    const [sendKeyModalVisible, setSendKeyModalVisible] = useState(false);
    const [onboardingDownloadModalOpen, setOnboardingDownloadModalOpen] = useState(false);
    const [onboardingDownloadModalFileName, setOnboardingDownloadModalFileName] = useState('');
    const [sendingApiKey, setSendingApiKey] = useState(false); // 发送API Key的loading状态
    const [uploading, setUploading] = useState(false);
    const [hubZipcodeInputs, setHubZipcodeInputs] = useState<Record<number, string>>({});
    const [financeContacts, setFinanceContacts] = useState<Array<{ name: string; email: string }>>([
        { name: '', email: '' }
    ]);
    const [submitting, setSubmitting] = useState(false); // 防止重复提交
    const [accountModalVisible, setAccountModalVisible] = useState(false);
    const [logModalVisible, setLogModalVisible] = useState(false);
    // 新建客户时，临时保存上传的文件信息（上传到S3后，在保存客户时一起传给后端）
    const [pendingDocuments, setPendingDocuments] = useState<Array<{ objectKey: string; filename: string; fileSize: number }>>([]);
    // 编辑客户时，记录待删除的文件（保存时才真正删除）
    const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());

    const [settlementMode, setSettlementMode] = useState<number | undefined>(undefined);
    const [isTimeZoneLocked, setIsTimeZoneLocked] = useState(false);
    const title = mode === 'create' ? t('central.channels.pageDetails.new') : t('central.channels.pageDetails.clientDetails');
    // CRM 资源码（替代原财务 / 总部角色硬编码）。基本信息 / 联系人 / 履约要求三个 Tab 四个角色集均持有，且承载必填字段，前端不按码隐藏
    const canManageSettlement = hasResource([ResourceCode.crm.CRM_CHANNEL_TAB_SETTLEMENT]);
    const canDownloadOnboardingDocuments = hasResource([ResourceCode.crm.CRM_CHANNEL_FILE_DOWNLOAD]);
    const canUploadFile = hasResource([ResourceCode.crm.CRM_CHANNEL_FILE_UPLOAD]) && !readOnly;
    const canDeleteFile = hasResource([ResourceCode.crm.CRM_CHANNEL_FILE_DELETE]) && !readOnly;
    const canAddFinanceContact = hasResource([ResourceCode.crm.CRM_CHANNEL_FINANCE_CONTACT_ADD]) && !readOnly;
    const canDeleteFinanceContact = hasResource([ResourceCode.crm.CRM_CHANNEL_FINANCE_CONTACT_DELETE]) && !readOnly;
    const canSendKey = hasResource([ResourceCode.crm.CRM_CHANNEL_SEND_KEY]);
    const canManageCustomerAccounts = hasResource([ResourceCode.crm.CRM_CHANNEL_CUSTOMER_ACCOUNT]);
    const canViewCustomerLogs = hasResource([ResourceCode.crm.CRM_CHANNEL_CUSTOMER_LOG]);
    const { settlementTrackingPoints, loading: trackingPointsLoading } = useAllSettlementTrackingPoints();
    const { contractingParties, loading: contractingPartiesLoading } = useAllContractingParties();
    // 使用 state 保存当前有效的 channelId，用于新建客户后上传入驻资料等操作
    const [currentChannelId, setCurrentChannelId] = useState<number | null>(null);
    const channelId = channel?.id ? parseInt(channel.id) : (currentChannelId || null);
    const { data: completeInfo, loading: infoLoading, refetch: refetchInfo } = useCompleteChannelInfo(channelId);
    const { data: documentsData, refetch: refetchDocuments } = useOnboardingDocuments(channelId);
    const { data: allHubsData } = useAllAvailablePickupHubs();
    const { data: configuredHubsData, loading: configuredHubsLoading, refetch: refetchConfiguredHubs } = useConfiguredPickupHubs(channelId);
    const [saveExtendedInfo] = useSaveChannelExtendedInfo();
    const [saveChannel] = useSaveChannel();
    const [updateChannelPortalConfig] = useUpdateChannelPortalConfig();
    const [uploadDocument] = useUploadOnboardingDocument();
    const [deleteDocument] = useDeleteOnboardingDocument();
    const [enqueueOnboardingDownload, { loading: enqueueOnboardingDownloadLoading }] =
        useEnqueueChannelOnboardingDocumentDownload();
    const [sendApiKey] = useSendApiKey();
    const [saveHubConfigs] = useSaveOrUpdatePickupHubConfigs();
    const [validateZipcodesExist] = useValidateZipcodesExist();
    const [isZipcodeInHubAreas] = useIsZipcodeInHubAreas();

    // 仅展示 listSettlementTrackingPointOptions 接口返回的选项，value 如 "60,80" 对应 label 如 "DELIVERED/RETURNED"
    const settlementTrackingPointOptions = React.useMemo(() => {
        return settlementTrackingPoints.map(point => ({
            value: point.value,
            label: point.label
        }));
    }, [settlementTrackingPoints]);

    // 签约主体下拉选项：后端返回 value=id(string)，转为 number 以匹配表单值类型
    const contractingPartyOptions = React.useMemo(() => {
        return contractingParties.map(party => ({
            value: Number(party.value),
            label: party.label
        }));
    }, [contractingParties]);

    // Reset form when Channel data or mode changes
    // 使用 useRef 来跟踪是否已经初始化过表单，避免保存后刷新覆盖用户修改
    const formInitializedRef = React.useRef<number | null>(null);
    // 使用 useRef 来同步标记提交状态，避免 onBlur 和提交验证重复弹窗
    const isSubmittingRef = React.useRef<boolean>(false);
    // 使用 useRef 来跟踪交邮点输入框是否已初始化
    // 交邮点输入框使用独立的 state，不受 Form 管理，需要单独跟踪初始化状态
    const hubZipcodeInitializedRef = React.useRef<number | null>(null);

    // 监听抽屉关闭，重置初始化标记与提交状态（防止再次打开时按钮仍处于禁用）
    useEffect(() => {
        if (!visible) {
            // 抽屉关闭时，重置所有初始化标记，确保下次打开时重新加载
            formInitializedRef.current = null;
            hubZipcodeInitializedRef.current = null;
            isSubmittingRef.current = false;
            setSubmitting(false);
            setFinanceContacts([{ name: '', email: '' }]);
            setIsTimeZoneLocked(false);
        }
    }, [visible]);

    // 第一个 useEffect：打开抽屉时重置状态并触发数据查询
    useEffect(() => {
        if (visible && channelId && channel) {
            // 编辑模式：重置所有表单和状态
            form.resetFields();
            setActiveTab('basic');
            setVisitedTabs(new Set(['basic'])); // 重置访问过的标签页记录
            setConfirmationChars(['', '', '', '']);
            setConfirmationInputs(['', '', '', '']);
            setSendKeyDisabled(true);
            setSendKeyModalVisible(false);
            setSendingApiKey(false);
            setHubZipcodeInputs({}); // 重置交邮点输入框，等待数据重新加载
            setSettlementMode(undefined); // 重置 settlementMode state
            setPendingDocuments([]); // 重置临时上传的文件列表
            setPendingDeletions(new Set()); // 重置待删除的文件列表
            setFinanceContacts([{ name: '', email: '' }]);

            // 重新从数据库查询最新数据（只有在channelId存在时才调用）
            if (channelId) {
                refetchInfo().catch(() => {}); // skip 时会返回 rejected promise，忽略错误
                refetchDocuments().catch(() => {});
                refetchConfiguredHubs().catch(() => {});
            }
        } else if (visible && !channel) {
            // 新建客户时的初始化
            // 先重置 currentChannelId，确保不会进入编辑模式的分支
            setCurrentChannelId(null);
            form.resetFields();
            setActiveTab('basic');
            setVisitedTabs(new Set(['basic']));
            setConfirmationChars(['', '', '', '']);
            setConfirmationInputs(['', '', '', '']);
            setSendKeyDisabled(true);
            setSendKeyModalVisible(false);
            setSendingApiKey(false);
            setPendingDocuments([]); // 重置临时上传的文件列表
            setPendingDeletions(new Set()); // 重置待删除的文件列表
            setHubZipcodeInputs({}); // 重置交邮点输入框
            setFinanceContacts([{ name: '', email: '' }]);
            setIsTimeZoneLocked(false);
            // 新建客户时，ID字段显示为空，后端会自动生成数据库自增ID
            form.setFieldsValue({
                id: undefined, // 不设置ID，显示为空，后端会自动生成
                isAutoGenerate: false,
                allowQueryAbnormalWeighingInfo: false,
                status: 1,
                responsibleNode: 1,
                customerType: 1,
                pickupHubMatchingType: 1, // 默认选择揽收范围匹配
                dimDivisor: 166,
                // 结算配置默认值
                billingCycleConfiguration: 1,
                weightCalculationMode: 0,
                settlementMode: 0,
                billUnitType: 0,
                timeZoneIana: 'Asia/Shanghai',
                settlementCustomerType: 2,
                trackDiscontinuationAssessmentType: 2,
            });
        }
    }, [visible, channelId, channel, form, refetchInfo, refetchDocuments, refetchConfiguredHubs]);

    // 第二个 useEffect：数据加载完成后填充表单
    useEffect(() => {
        if (visible && channel && completeInfo?.getCompleteChannelInfo && !infoLoading) {
            const currentChannelId = channel?.id ? parseInt(channel.id) : null;

            // 每次数据加载完成后都填充表单（确保总是使用数据库中的最新数据）
            // 如果 formInitializedRef 为 null 或 channelId 变化，说明需要初始化或切换了客户
            // 由于抽屉关闭时会重置 formInitializedRef.current = null，所以每次打开都会重新填充
            // 这样确保总是使用数据库中的最新数据
            // 注意：如果正在提交中（submitting），不要重新填充表单，避免覆盖用户正在输入的数据
            if (!submitting && (formInitializedRef.current === null || formInitializedRef.current !== currentChannelId)) {
                formInitializedRef.current = currentChannelId;
                const info = completeInfo.getCompleteChannelInfo;
                // 准备表单值，确保数字字段是数字类型
                // 处理 gri 字段
                let griValue: number | undefined = undefined;
                const griStr = info.channelSettlement?.gri;
                if (griStr !== undefined && griStr !== null && String(griStr).trim() !== '') {
                    const num = Number(griStr);
                    console.log('griValue', num, 'from', griStr, 'isNaN:', isNaN(num));
                    if (!isNaN(num)) {
                        console.log('griValue setting', num);
                        griValue = num;
                    }
                }

                const formValues: any = {
                    // 基础信息（channel表）
                    id: info.channel.id,
                    channelName: info.channel.channelName,
                    companyName: info.channel.companyName,
                    companyAddress: info.channel.companyAddress,
                    contactName: info.channel.contactName,
                    contactPhone: info.channel.contactPhone,
                    email: info.channel.email,
                    taxRate: info.channel.taxRate,
                    taxId: info.channel.taxId,
                    isAutoGenerate: info.channel.isAutoGenerate,
                    allowQueryAbnormalWeighingInfo: info.channelPortalConfig?.allowQueryAbnormalWeighingInfo ?? false,
                    status: info.channel.status,
                    remark: info.channel.remark,
                    // 扩展基础信息（channel_base表）
                    bdName: info.channelBase?.bdName,
                    englishShortName: info.channelBase?.englishShortName,
                    responsibleNode: info.channelBase?.responsibleNode ?? 1,
                    customerType: info.channelBase?.customerType ?? 1,
                    pickupHubMatchingType: info.channelBase?.pickupHubMatchingType ?? 1, // 默认选择揽收范围匹配
                    fixedPickupHubId: info.channelBase?.fixedPickupHubId ?? undefined,
                    // 签约主体：若引用的主体已被删除（不在下拉选项中），由下方 useEffect 统一置空
                    contractingPartyId: info.channelBase?.contractingPartyId != null ? Number(info.channelBase.contractingPartyId) : undefined,
                    // 结算配置（channel_settlement表）
                    netDay: info.channelSettlement?.netDay !== undefined && info.channelSettlement?.netDay !== null ? Number(info.channelSettlement.netDay) : undefined,
                    orderQuantityLimit: info.channelSettlement?.orderQuantityLimit !== undefined && info.channelSettlement?.orderQuantityLimit !== null ? Number(info.channelSettlement.orderQuantityLimit) : undefined,
                    orderDailyLimit: info.channelSettlement?.orderDailyLimit !== undefined && info.channelSettlement?.orderDailyLimit !== null ? String(info.channelSettlement.orderDailyLimit) : undefined,
                    orderCycleLimit: info.channelSettlement?.orderCycleLimit !== undefined && info.channelSettlement?.orderCycleLimit !== null ? String(info.channelSettlement.orderCycleLimit) : undefined,
                    gri: griValue,
                    dimDivisor: info.channelSettlement?.dimDivisor ?? 166,
                    // 结算客户配置（channel_settlement表的冗余字段）
                    settlementCustomerType: info.channelSettlement?.settlementCustomerType ?? channel?.settlementCustomerType ?? 2,
                    settlementTrackingPoint: info.channelSettlement?.settlementTrackingPoint ?? channel?.settlementTrackingPoint ?? undefined,
                    billingCycleConfiguration: info.channelSettlement?.billingCycleConfiguration ?? channel?.billingCycleConfiguration ?? 1,
                    weightCalculationMode: info.channelSettlement?.weightCalculationMode ?? channel?.weightCalculationMode ?? 0,
                    weightCalculationThresholdX: info.channelSettlement?.weightCalculationThresholdX ?? channel?.weightCalculationThresholdX,
                    settlementMode: info.channelSettlement?.settlementMode ?? channel?.settlementMode ?? 0,
                    billType: info.channelSettlement?.billType ? info.channelSettlement.billType.split(',').map(v => parseInt(v)).filter(v => !isNaN(v)) : (channel?.billType ?? []),
                    billUnitType: info.channelSettlement?.billUnitType ?? channel?.billUnitType ?? 0,
                    timeZoneIana: info.channelSettlement?.timeZoneIana ?? channel?.timeZoneIana ?? 'Asia/Shanghai',
                    schemeLimitNode: info.channelSettlement?.schemeLimitNode ?? channel?.schemeLimitNode,
                    // 联系人信息（channel_contact表）
                    financeContactName: info.channelContact?.financeContactName,
                    financeContactEmail: info.channelContact?.financeContactEmail,
                    itContactName: info.channelContact?.itContactName,
                    itContactEmail: info.channelContact?.itContactEmail,
                    // 履约要求（channel_fulfillment表）
                    // 时间值存储为小时，需要根据显示单位转换为显示值
                    forwardTimeValue: info.channelFulfillment?.forwardTimeValue && info.channelFulfillment?.forwardTimeDisplayUnit
                        ? (info.channelFulfillment.forwardTimeDisplayUnit === 2
                            ? info.channelFulfillment.forwardTimeValue / 24
                            : info.channelFulfillment.forwardTimeValue)
                        : undefined,
                    forwardTimeDisplayUnit: info.channelFulfillment?.forwardTimeDisplayUnit ?? 2,
                    failureMethod: info.channelFulfillment?.failureMethod,
                    storageTimeValue: info.channelFulfillment?.storageTimeValue && info.channelFulfillment?.storageTimeDisplayUnit
                        ? (info.channelFulfillment.storageTimeDisplayUnit === 2
                            ? info.channelFulfillment.storageTimeValue / 24
                            : info.channelFulfillment.storageTimeValue)
                        : undefined,
                    storageTimeDisplayUnit: info.channelFulfillment?.storageTimeDisplayUnit ?? 2,
                    returnTimeValue: info.channelFulfillment?.returnTimeValue && info.channelFulfillment?.returnTimeDisplayUnit
                        ? (info.channelFulfillment.returnTimeDisplayUnit === 2
                            ? info.channelFulfillment.returnTimeValue / 24
                            : info.channelFulfillment.returnTimeValue)
                        : undefined,
                    returnTimeDisplayUnit: info.channelFulfillment?.returnTimeDisplayUnit ?? 2,
                    trackDiscontinuationAssessmentType:
                        info.channelFulfillment?.trackDiscontinuationAssessmentType ?? 2,
                    trackDiscontinuationAssessmentDays:
                        info.channelFulfillment?.trackDiscontinuationAssessmentDays ?? undefined,
                    // 如果已有结算配置，则设置 isSetSettlementConfig 为 true
                    // 判断标准：根据结算模式判断
                    // PAY_REAL_TIME (1): 只需要 settlementMode 和 weightCalculationMode 等通用字段
                    // PAY_LATER_BILL (0): 需要 settlementTrackingPoint、billingCycleConfiguration、settlementCustomerType 等字段
                    isSetSettlementConfig: (() => {
                        if (!info.channelSettlement) {
                            return false;
                        }
                        const settlementMode = info.channelSettlement.settlementMode ?? channel?.settlementMode ?? 0;
                        const isPayRealtime = settlementMode === 1;
                        
                        if (isPayRealtime) {
                            // 实时结算：只需要 settlementMode 和 weightCalculationMode 等通用字段
                            return info.channelSettlement.settlementMode != null &&
                                   (info.channelSettlement.weightCalculationMode != null ||
                                    info.channelSettlement.timeZoneIana != null ||
                                    info.channelSettlement.billUnitType != null);
                        } else {
                            // 账期结算：需要 settlementTrackingPoint、billingCycleConfiguration、settlementCustomerType 等字段
                            return info.channelSettlement.settlementTrackingPoint != null ||
                                   info.channelSettlement.billingCycleConfiguration != null ||
                                   info.channelSettlement.settlementCustomerType != null ||
                                   info.channelSettlement.weightCalculationMode != null ||
                                   info.channelSettlement.settlementMode != null;
                        }
                    })(),
                };

                const persistedTimeZoneIana =
                    info.channelSettlement?.timeZoneIana ?? channel?.timeZoneIana;
                setIsTimeZoneLocked(!!persistedTimeZoneIana);

                // 初始化财务联系人列表（支持1~10个）
                try {
                    const rawJson = info.channelContact?.financeContactsJson;
                    let contacts: Array<{ name: string; email: string }> = [];

                    if (rawJson) {
                        const parsed = JSON.parse(rawJson);
                        if (Array.isArray(parsed)) {
                            contacts = parsed
                                .map((c: any) => ({
                                    name: (c?.name ?? '').toString().trim(),
                                    email: (c?.email ?? '').toString().trim(),
                                }))
                                // 过滤掉姓名和邮箱都为空的记录
                                .filter((c) => c.name || c.email);
                        }
                    }

                    if (contacts.length === 0 && info.channelContact) {
                        const legacyName = info.channelContact.financeContactName ?? '';
                        const legacyEmail = info.channelContact.financeContactEmail ?? '';
                        if (legacyName || legacyEmail) {
                            contacts = [{ name: legacyName, email: legacyEmail }];
                        }
                    }

                    if (contacts.length === 0) {
                        contacts = [{ name: '', email: '' }];
                    }

                    setFinanceContacts(contacts.slice(0, 10));
                } catch (e) {
                    // JSON 解析失败时，回退到单条记录或空白一条
                    const legacyName = info.channelContact?.financeContactName ?? '';
                    const legacyEmail = info.channelContact?.financeContactEmail ?? '';
                    const contacts: Array<{ name: string; email: string }> =
                        legacyName || legacyEmail
                            ? [{ name: legacyName, email: legacyEmail }]
                            : [{ name: '', email: '' }];
                    setFinanceContacts(contacts);
                }

                form.setFieldsValue(formValues);
                // 同步设置 settlementMode state，以便动态显示/隐藏字段
                const loadedSettlementMode = info.channelSettlement?.settlementMode ?? channel?.settlementMode ?? 0;
                setSettlementMode(loadedSettlementMode);
            }
        }
    }, [visible, channel, form, completeInfo, infoLoading, submitting]);

    const handleSubmit = async () => {
        // 防止重复提交
        if (submitting || isSubmittingRef.current) {
            console.warn('Submit already in progress, ignoring duplicate call');
            return;
        }

        // 在函数最开始就设置 submitting 和 ref，这样当输入框失去焦点时，onBlur 会跳过验证
        // 使用 ref 是因为它的更新是同步的，可以立即被 onBlur 检测到
        isSubmittingRef.current = true;
        setSubmitting(true);

        try {
            let values;
            // 避免通过基础信息校验但联系人信息不合法时仍然保存基础信息
            let primaryFinanceContact: { name: string; email: string } | null = null;
            let financeContactsJson: string | undefined;
            try {
                console.log('开始验证所有表单字段...');
                values = await form.validateFields();
                console.log('表单验证通过，values:', values);
            } catch (error: any) {
                console.error('表单验证失败:', error);
                if (error?.errorFields && error.errorFields.length > 0) {
                    const firstErrorField = error.errorFields[0].name[0];
                    console.log('第一个错误字段:', firstErrorField);
    
                    const tabFieldGroups: Record<string, string[]> = {
                        basic: [
                            'channelName', 'companyName', 'isAutoGenerate', 'status', 'contractingPartyId', 'bdName',
                            'englishShortName', 'responsibleNode', 'customerType', 'pickupHubMatchingType'
                        ],
                        settlement: [
                            'settlementCustomerType', 'settlementTrackingPoint',
                            'billingCycleConfiguration', 'weightCalculationMode',
                            'settlementMode', 'billType', 'timeZoneIana', 'billUnitType',
                            'schemeLimitNode', 'dimDivisor', 'netDay', 'orderQuantityLimit',
                            'orderDailyLimit', 'orderCycleLimit', 'gri'
                        ],
                        contact: [
                            'contactName', 'contactPhone', 'email', 'financeContactName',
                            'financeContactEmail', 'itContactName', 'itContactEmail'
                        ],
                        fulfillment: [
                            'forwardTimeValue', 'forwardTimeDisplayUnit', 'failureMethod',
                            'storageTimeValue', 'storageTimeDisplayUnit',
                            'returnTimeValue', 'returnTimeDisplayUnit',
                            'trackDiscontinuationAssessmentType', 'trackDiscontinuationAssessmentDays'
                        ]
                    };

                    if (tabFieldGroups.basic.includes(firstErrorField)) {
                        setActiveTab('basic');
                    } else if (tabFieldGroups.settlement.includes(firstErrorField)) {
                        setActiveTab('settlement');
                    } else if (tabFieldGroups.contact.includes(firstErrorField)) {
                        setActiveTab('contact');
                    } else if (tabFieldGroups.fulfillment.includes(firstErrorField)) {
                        setActiveTab('fulfillment');
                    }
                }
                // 重新抛出错误，让外层 catch 处理
                throw error;
            }

            // 验证入驻资料是否已上传（必填）- 新建和编辑模式都需要检查
            // 注意：编辑模式下，documents 已经过滤掉了待删除的文件，所以直接使用 documents
            const currentDocuments = mode === 'create' ? pendingDocuments : documents;
            
            if (currentDocuments.length === 0) {
                // 切换到基础信息标签页
                setActiveTab('basic');
                const errorMessage = String(t('central.channels.messages.onboardingDocumentsRequired'));
                // 只抛出错误，不在这里显示消息，让 catch 块统一处理
                throw new Error(errorMessage);
            }

            // 验证交邮点配置（在保存之前验证，避免保存成功但验证失败的情况）
            if (values.pickupHubMatchingType === 2) {
                // 交邮点改为按HUB可选：只校验非空项的格式，并要求至少配置一个
                const allHubs = allHubsData?.getAllAvailablePickupHubs || [];
                // 遍历 hubZipcodeInputs 而非 allHubs，使校验集合与下方提交的全量快照严格一致；
                // HUB 名仅用于拼错误消息，取不到时沿用 `HUB{id}` 兜底
                // key 统一用 String：后端 Long 标量序列化为字符串（见 GraphQLScalarConfiguration），
                // hub.id 运行时是 "12" 而非 12，用数字做 key 会导致 Map 永远取不到、错误提示退化成 HUB{id}
                const hubCodeMap = new Map<string, string>();
                allHubs.forEach(hub => {
                    if (hub.id) {
                        hubCodeMap.set(String(hub.id), hub.name?.split(' ')[0]?.toUpperCase() || `HUB${hub.id}`);
                    }
                });

                const hubsWithInvalidFormat: string[] = [];
                let filledCount = 0;

                Object.entries(hubZipcodeInputs).forEach(([hubIdStr, rawZipcode]) => {
                    const zipcode = rawZipcode?.trim();
                    // 留空表示该HUB不参与揽收匹配，不再报错
                    if (!zipcode) {
                        return;
                    }
                    filledCount += 1;

                    // 检查格式（必须是5位数字）
                    if (!/^\d{5}$/.test(zipcode)) {
                        hubsWithInvalidFormat.push(hubCodeMap.get(hubIdStr) || `HUB${hubIdStr}`);
                    }
                });

                // 收集所有错误信息
                const errorMessages: string[] = [];
                if (filledCount === 0) {
                    errorMessages.push(String(t('central.channels.messages.pickupPointAtLeastOne')));
                }
                if (hubsWithInvalidFormat.length > 0) {
                    errorMessages.push(t('central.channels.messages.pickupPointInvalidFormat', { hubs: hubsWithInvalidFormat.join(', ') }));
                }

                if (errorMessages.length > 0) {
                    // 切换到基础信息标签页，显示交邮点配置区域
                    setActiveTab('basic');
                    const errorMessage = errorMessages.join('\n');
                    // 只抛出错误，不在这里显示消息，让 catch 块统一处理
                    throw new Error(errorMessage);
                }

                // 构建配置列表，用于后续验证
                const configs = Object.entries(hubZipcodeInputs)
                    .filter(([_, zipcode]) => zipcode && zipcode.trim().length > 0)
                    .map(([hubId, zipcode]) => {
                        const trimmedZipcode = zipcode.trim();
                        return {
                            hubId: parseInt(hubId),
                            zipcodes: [trimmedZipcode] // 只保存一个zipcode
                        };
                    });

                // 检查重复的zipcode（在保存之前验证）
                if (configs.length > 0) {
                    const zipcodes = configs.map(c => c.zipcodes[0]);
                    const duplicateZipcodes = zipcodes.filter((zipcode, index) => zipcodes.indexOf(zipcode) !== index);
                    if (duplicateZipcodes.length > 0) {
                        const uniqueDuplicates = [...new Set(duplicateZipcodes)];
                        setActiveTab('basic');
                        throw new Error(String(t('central.channels.messages.pickupPointCannotDuplicateWithList', { zipcodes: uniqueDuplicates.join(', ') })));
                    }

                    // 验证zipcode是否在系统基础数据中（在保存之前验证）
                    const zipcodesToValidate = configs.map(c => c.zipcodes[0]);
                    const { data: validateData } = await validateZipcodesExist({
                        variables: { zipcodes: zipcodesToValidate }
                    });

                    const invalidZipcodes = validateData?.validateZipcodesExist || [];
                    if (invalidZipcodes.length > 0) {
                        setActiveTab('basic');
                        throw new Error(String(t('central.channels.messages.zipcodeNotInSystemConfigWithList', { zipcodes: invalidZipcodes.join(', ') })));
                    }

                    // 验证每个zipcode是否在hub的揽收/可派送区域内（在保存之前验证）
                    // 先收集所有不在hub区域内的zipcode，然后一次性弹出确认框
                    const invalidHubZipcodes: string[] = [];
                    for (const config of configs) {
                        const zipcode = config.zipcodes[0];
                        const { data: hubAreaData } = await isZipcodeInHubAreas({
                            variables: { zipcode }
                        });

                        if (hubAreaData?.isZipcodeInHubAreas === false) {
                            invalidHubZipcodes.push(zipcode);
                        }
                    }

                    // 如果有不在hub区域内的zipcode，一次性弹出确认框
                    if (invalidHubZipcodes.length > 0) {
                        const confirmed = await new Promise<boolean>((resolve) => {
                            Modal.confirm({
                                title: t('central.channels.messages.confirmTitle'),
                                content: invalidHubZipcodes.length === 1
                                    ? t('central.channels.messages.zipcodeNotInHubAreasSingle', { zipcode: invalidHubZipcodes[0] })
                                    : t('central.channels.messages.zipcodeNotInHubAreasMultiple', { zipcodes: invalidHubZipcodes.join(', ') }),
                                okText: t('components.button.confirm'),
                                cancelText: t('components.button.cancel'),
                                onOk: () => {
                                    resolve(true);
                                },
                                onCancel: () => {
                                    resolve(false);
                                }
                            });
                        });

                        if (!confirmed) {
                            // 用户取消，阻止提交
                            setActiveTab('basic');
                            throw new Error(String(t('central.channels.messages.submitCancelled')));
                        }
                    }
                }
            }

            // 在保存任何信息之前，统一校验财务联系人信息，避免只保存基础信息但联系人信息不合法的情况
            if (mode === 'create' || visitedTabs.has('contact') || mode === 'edit') {
                const cleanedFinanceContacts = financeContacts
                    .map((c) => ({
                        name: c.name?.trim() || '',
                        email: c.email?.trim() || '',
                    }))
                    .filter((c) => c.name || c.email); // 过滤姓名+邮箱都为空的无效记录

                // 基础数量校验：至少1组联系人
                if (cleanedFinanceContacts.length === 0) {
                    setActiveTab('contact');
                    throw new Error(String(t('central.channels.messages.financeContactRequired')));
                }

                // 邮箱完整性校验：每组必须填邮箱
                const incompleteContact = cleanedFinanceContacts.find((c) => !c.email);
                if (incompleteContact) {
                    setActiveTab('contact');
                    throw new Error(String(t('central.channels.messages.financeContactEmailRequired')));
                }

                // 邮箱格式合法性校验
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                const invalidEmailContact = cleanedFinanceContacts.find((c) => !emailRegex.test(c.email));
                if (invalidEmailContact) {
                    setActiveTab('contact');
                    throw new Error(String(t('central.channels.messages.financeContactEmailInvalid')));
                }

                // 重复性校验（当前填写的列表内不能重复，忽略大小写）
                const lowerCaseEmails = cleanedFinanceContacts.map((c) => c.email.toLowerCase());
                const duplicateLocalEmails = lowerCaseEmails.filter((email, index) =>
                    lowerCaseEmails.indexOf(email) !== index
                );
                if (duplicateLocalEmails.length > 0) {
                    setActiveTab('contact');
                    throw new Error(String(t('central.channels.messages.financeContactEmailDuplicate')));
                }

                // 校验通过，记录结果，后续用于构建扩展信息
                primaryFinanceContact = cleanedFinanceContacts[0];
                financeContactsJson = JSON.stringify(cleanedFinanceContacts);
            }

            // 1. 保存基础channel信息
            const channelInput: ChannelInput = {
                id: values.id ? String(values.id) : undefined,
                channelName: String(values.channelName),
                companyName: String(values.companyName),
                companyAddress: values.companyAddress ? String(values.companyAddress) : undefined,
                contactName: values.contactName ? String(values.contactName) : undefined,
                contactPhone: values.contactPhone ? String(values.contactPhone) : undefined,
                email: values.email ? String(values.email) : undefined,
                taxRate: values.taxRate ? String(values.taxRate) : undefined,
                taxId: values.taxId ? String(values.taxId) : undefined,
                isAutoGenerate: Boolean(values.isAutoGenerate),
                // 新建客户时：只有财务账号才设置 isSetSettlementConfig 为 true，以触发账期生成
                // 编辑客户时：如果之前没有开过户，但用户填写了结算配置信息，则设置为 true 以触发开户
                isSetSettlementConfig: mode === 'create'
                    ? (canManageSettlement ? true : false) // 新建客户时，仅财务/总部管理员保存结算配置
                    : (() => {
                        // 如果表单中已经有值（说明之前已开户），使用表单中的值
                        if (values.isSetSettlementConfig === true) {
                            return true;
                        }
                        // 如果之前没有开过户，但用户填写了结算配置信息，则设置为 true 以触发开户
                        // 判断标准：如果访问过结算标签页，说明用户填写了结算配置信息
                        if (visitedTabs.has('settlement')) {
                            const settlementMode = values.settlementMode !== undefined ? Number(values.settlementMode) : undefined;
                            const isPayRealtime = settlementMode === 1;
                            
                            if (isPayRealtime) {
                                // 实时结算：只需要 settlementMode 和 weightCalculationMode 等通用字段
                                if (values.settlementMode != null &&
                                    (values.weightCalculationMode != null ||
                                     values.timeZoneIana != null ||
                                     values.billUnitType != null)) {
                                    return true;
                                }
                            } else {
                                // 账期结算：需要 settlementTrackingPoint、billingCycleConfiguration、settlementCustomerType 等字段
                                if (values.settlementTrackingPoint != null ||
                                    values.billingCycleConfiguration != null ||
                                    values.settlementCustomerType != null ||
                                    values.weightCalculationMode != null ||
                                    values.settlementMode != null) {
                                    return true;
                                }
                            }
                        }
                        // 默认返回 false
                        return false;
                    })(),
                settlementCustomerType: values.settlementCustomerType ?? 2,
                settlementTrackingPoint: values.settlementTrackingPoint != null && values.settlementTrackingPoint !== '' ? (Array.isArray(values.settlementTrackingPoint) ? values.settlementTrackingPoint.join(',') : String(values.settlementTrackingPoint)) : null,
                billingCycleConfiguration: values.billingCycleConfiguration,
                weightCalculationMode: values.weightCalculationMode,
                weightCalculationThresholdX: values.weightCalculationThresholdX !== undefined && values.weightCalculationThresholdX !== null
                    ? Number(values.weightCalculationThresholdX)
                    : undefined,
                settlementMode: values.settlementMode,
                billType: (() => {
                    // ChannelTypeInput.billType 期望 List<Int>?，所以需要确保是数组
                    const billTypeValue = values.billType;
                    if (billTypeValue === undefined || billTypeValue === null) {
                        return undefined;
                    }
                    // 如果是数组，直接返回（过滤掉无效值）
                    if (Array.isArray(billTypeValue)) {
                        const filtered = billTypeValue.filter(v => v !== undefined && v !== null && v !== '' && !isNaN(Number(v)));
                        // 如果过滤后为空数组，返回 undefined 而不是空数组
                        if (filtered.length === 0) {
                            return undefined;
                        }
                        // 转换为数字数组
                        const numbers = filtered.map(v => Number(v)).filter(v => !isNaN(v));
                        return numbers.length > 0 ? numbers : undefined;
                    }
                    // 如果是字符串（可能是从其他地方来的），尝试解析
                    if (typeof billTypeValue === 'string') {
                        const parsed = billTypeValue.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                        return parsed.length > 0 ? parsed : undefined;
                    }
                    // 如果是数字，转换为数组
                    if (typeof billTypeValue === 'number' && !isNaN(billTypeValue)) {
                        return [billTypeValue];
                    }
                    return undefined;
                })(),
                billUnitType: values.billUnitType,
                timeZoneIana: values.timeZoneIana,
                schemeLimitNode: values.schemeLimitNode || undefined,
                status: values.status,
                remark: values.remark ? String(values.remark) : undefined
            };

            // 调试：检查 billType 的类型，确保是数组或 undefined
            if (channelInput.billType !== undefined) {
                console.log('saveChannel - billType before send:', {
                    type: typeof channelInput.billType,
                    isArray: Array.isArray(channelInput.billType),
                    value: channelInput.billType,
                    stringified: JSON.stringify(channelInput.billType)
                });
                // 确保 billType 是数组类型，而不是其他类型
                const billTypeValue: any = channelInput.billType;
                if (!Array.isArray(billTypeValue)) {
                    console.warn('billType is not an array, converting...', billTypeValue);
                    // 如果类型不对，尝试修复
                    if (typeof billTypeValue === 'string') {
                        channelInput.billType = billTypeValue.split(',').map((v: string) => parseInt(v.trim())).filter((v: number) => !isNaN(v));
                    } else if (typeof billTypeValue === 'number') {
                        channelInput.billType = [billTypeValue];
                    } else {
                        channelInput.billType = undefined;
                    }
                }
            }

            // 调试：输出完整的 channelInput 以便排查问题
            console.log('saveChannel - full channelInput:', JSON.stringify(channelInput, null, 2));

            let savedChannel;
            try {
                savedChannel = await saveChannel({
                    variables: { input: channelInput }
                });
            } catch (error: any) {
                console.error('saveChannel mutation error:', error);
                // 提取详细的错误信息
                let errorMessage = t('central.channels.messages.saveChannelBaseInfoFailed');
                if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                    const graphQLError = error.graphQLErrors[0];
                    errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
                    console.error('GraphQL error details:', {
                        message: graphQLError.message,
                        extensions: graphQLError.extensions,
                        path: graphQLError.path,
                        locations: graphQLError.locations
                    });
                } else if (error?.networkError) {
                    errorMessage = error.networkError.message || errorMessage;
                    console.error('Network error details:', error.networkError);
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                message.error(errorMessage);
                return;
            }

            if (channelInput.isSetSettlementConfig && channelInput.timeZoneIana) {
                setIsTimeZoneLocked(true);
            }

            const savedChannelId = savedChannel.data?.saveChannel?.id
                ? parseInt(savedChannel.data.saveChannel.id)
                : (channel?.id ? parseInt(channel.id) : null);

            if (!savedChannelId || isNaN(savedChannelId)) {
                console.error('savedChannelId is invalid:', {
                    savedChannelId,
                    savedChannelData: savedChannel.data,
                    channelId: channel?.id
                });
                message.error(t('central.channels.messages.saveChannelBaseInfoFailed'));
                return;
            }

            // 2. 保存扩展信息
            // 获取现有的入驻资料JSON，避免覆盖
            let onboardingDocumentsJson: string | undefined;
            if (mode === 'create') {
                // 新建客户时：通过 onboardingDocumentsJson 传递临时文件
                // 新上传的文件没有 uploadedAt，后端会自动补充
                const newDocuments = pendingDocuments.map(doc => ({
                    objectKey: doc.objectKey,
                    filename: doc.filename,
                    fileSize: doc.fileSize
                    // 注意：不包含 uploadedAt，后端会自动补充
                }));
                onboardingDocumentsJson = newDocuments.length > 0 ? JSON.stringify(newDocuments) : undefined;
                console.log('[ChannelFormDrawer] Create mode - onboardingDocumentsJson:', onboardingDocumentsJson);
                console.log('[ChannelFormDrawer] Create mode - pendingDocuments:', pendingDocuments);
            } else {
                // 编辑客户时：构建完整的文件列表
                // 1. 数据库中的文件（排除待删除的）
                const existingDocuments = (documentsData?.getOnboardingDocuments || []).filter(
                    doc => !pendingDeletions.has(doc.objectKey)
                );
                console.log('[ChannelFormDrawer] Edit mode - existingDocuments:', existingDocuments);
                console.log('[ChannelFormDrawer] Edit mode - pendingDeletions:', Array.from(pendingDeletions));
                // 2. 新上传的文件（没有 uploadedAt，后端会自动补充）
                const newDocuments = pendingDocuments.map(doc => ({
                    objectKey: doc.objectKey,
                    filename: doc.filename,
                    fileSize: doc.fileSize
                    // 注意：不包含 uploadedAt，后端会自动补充
                }));
                console.log('[ChannelFormDrawer] Edit mode - newDocuments:', newDocuments);
                // 3. 合并所有文件
                const allDocuments = [...existingDocuments, ...newDocuments];
                onboardingDocumentsJson = allDocuments.length > 0 ? JSON.stringify(allDocuments) : undefined;
                console.log('[ChannelFormDrawer] Edit mode - allDocuments:', allDocuments);
                console.log('[ChannelFormDrawer] Edit mode - onboardingDocumentsJson:', onboardingDocumentsJson);
            }

            try {
                // 预先转换 billType，确保是字符串类型
                let billTypeString: string | undefined = undefined;
                const billTypeValue = values.billType;
                if (billTypeValue !== undefined && billTypeValue !== null) {
                    if (Array.isArray(billTypeValue)) {
                        const filtered = billTypeValue.filter(v => v !== undefined && v !== null && v !== '');
                        if (filtered.length > 0) {
                            billTypeString = filtered.map(v => String(v)).join(',');
                        }
                    } else {
                        billTypeString = String(billTypeValue);
                    }
                }

                console.log('saveExtendedInfo - billType before send:', {
                    original: values.billType,
                    converted: billTypeString,
                    type: typeof billTypeString
                });

            // 构建扩展信息
            // 新建客户时：保存所有填写的信息（不管是否访问过标签页）
            // 编辑客户时：只保存访问过的标签页的数据（避免覆盖用户不想修改的数据）
            const extendedInfoVariables: any = {
                channelId: savedChannelId,
                channelBase: {
                    bdName: values.bdName,
                    englishShortName: values.englishShortName?.trim() || undefined,
                    onboardingDocumentsJson: onboardingDocumentsJson,
                    responsibleNode: values.responsibleNode,
                    customerType: values.customerType,
                    pickupHubMatchingType: values.pickupHubMatchingType,
                    // 仅当揽收HUB匹配类型 = 4 时才透传 fixedPickupHubId，避免脏数据
                    fixedPickupHubId: values.pickupHubMatchingType === 4 ? values.fixedPickupHubId : undefined,
                    // 签约主体（选填），随基础信息块一并保存
                    contractingPartyId: values.contractingPartyId != null ? Number(values.contractingPartyId) : undefined,
                }
            };

            // 结算配置：新建客户时仅财务/总部管理员保存，编辑客户时只有访问过才保存
            if ((mode === 'create' && canManageSettlement) || visitedTabs.has('settlement')) {
                const settlementMode = values.settlementMode !== undefined ? Number(values.settlementMode) : undefined;
                const isPayRealtime = settlementMode === 1;
                
                extendedInfoVariables.channelSettlement = {
                    netDay: values.netDay ? Number(values.netDay) : undefined,
                    // 只有在 PAY_LATER_BILL (0) 时才保存订单量限制相关字段
                    orderQuantityLimit: !isPayRealtime && values.orderQuantityLimit ? Number(values.orderQuantityLimit) : undefined,
                    orderDailyLimit: !isPayRealtime && values.orderDailyLimit ? Number(values.orderDailyLimit) : undefined,
                    orderCycleLimit: !isPayRealtime && values.orderCycleLimit ? Number(values.orderCycleLimit) : undefined,
                    gri: values.gri ? String(values.gri) : undefined,
                    dimDivisor: values.dimDivisor ? Number(values.dimDivisor) : undefined,
                    // 冗余字段（双写）
                    // 当选择 PAY_REAL_TIME 时，这些字段不发送（发送 undefined）
                    settlementTrackingPoint: !isPayRealtime && values.settlementTrackingPoint != null && values.settlementTrackingPoint !== '' ? (Array.isArray(values.settlementTrackingPoint) ? values.settlementTrackingPoint.join(',') : String(values.settlementTrackingPoint)) : (!isPayRealtime ? undefined : undefined),
                    billingCycleConfiguration: !isPayRealtime && values.billingCycleConfiguration !== undefined ? Number(values.billingCycleConfiguration) : undefined,
                    weightCalculationMode: values.weightCalculationMode !== undefined ? Number(values.weightCalculationMode) : undefined,
                    weightCalculationThresholdX: values.weightCalculationThresholdX !== undefined && values.weightCalculationThresholdX !== null ? Number(values.weightCalculationThresholdX) : undefined,
                    settlementMode: settlementMode,
                    timeZoneIana: values.timeZoneIana !== undefined ? String(values.timeZoneIana) : undefined,
                    schemeLimitNode: values.schemeLimitNode || undefined,
                    settlementCustomerType: !isPayRealtime && values.settlementCustomerType !== undefined ? Number(values.settlementCustomerType) : undefined,
                    billType: !isPayRealtime ? billTypeString : undefined,
                    billUnitType: values.billUnitType !== undefined ? Number(values.billUnitType) : undefined,
                };
            }

            // 联系人信息：新建客户时总是保存，编辑客户时总是保存（避免丢失已有数据）
            if (mode === 'create' || visitedTabs.has('contact') || mode === 'edit') {
                // 如果前面未成功计算出 primaryFinanceContact，说明校验未通过或逻辑异常，这里直接跳过，避免覆盖已有数据
                if (primaryFinanceContact && financeContactsJson) {
                    extendedInfoVariables.channelContact = {
                        financeContactName: primaryFinanceContact.name || undefined,
                        financeContactEmail: primaryFinanceContact.email || undefined,
                        financeContactsJson,
                        itContactName: values.itContactName?.trim() || undefined,
                        itContactEmail: values.itContactEmail?.trim() || undefined,
                    };
                }
            }

            // 履约要求：新建客户时总是保存，编辑客户时只有访问过才保存
            // 时间由后端按“数值+显示单位”统一转换为小时存储，前端只传用户输入的数值和单位
            if (mode === 'create' || visitedTabs.has('fulfillment')) {
                extendedInfoVariables.channelFulfillment = {
                    forwardTimeValue: values.forwardTimeValue ?? null,
                    forwardTimeDisplayUnit: values.forwardTimeDisplayUnit ?? null,
                    failureMethod: values.failureMethod ?? null,
                    storageTimeValue: values.storageTimeValue ?? null,
                    storageTimeDisplayUnit: values.storageTimeDisplayUnit ?? null,
                    returnTimeValue: values.returnTimeValue ?? null,
                    returnTimeDisplayUnit: values.returnTimeDisplayUnit ?? null,
                    trackDiscontinuationAssessmentType: values.trackDiscontinuationAssessmentType ?? 2,
                    trackDiscontinuationAssessmentDays:
                        values.trackDiscontinuationAssessmentType === 1
                            ? values.trackDiscontinuationAssessmentDays ?? null
                            : null,
                };
            }

            await saveExtendedInfo({
                variables: extendedInfoVariables
            });
            if (extendedInfoVariables.channelSettlement?.timeZoneIana) {
                setIsTimeZoneLocked(true);
            }

            await updateChannelPortalConfig({
                variables: {
                    channelId: savedChannelId,
                    allowQueryAbnormalWeighingInfo: Boolean(values.allowQueryAbnormalWeighingInfo),
                }
            });
            } catch (error: any) {
                console.error('saveExtendedInfo mutation error:', error);
                // 提取详细的错误信息
                let errorMessage = t('central.channels.messages.saveFailed');
                if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                    const graphQLError = error.graphQLErrors[0];
                    errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
                    console.error('GraphQL error details:', {
                        message: graphQLError.message,
                        extensions: graphQLError.extensions,
                        path: graphQLError.path,
                        locations: graphQLError.locations
                    });
                } else if (error?.networkError) {
                    errorMessage = error.networkError.message || errorMessage;
                    console.error('Network error details:', error.networkError);
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                message.error(errorMessage);
                return;
            }

            // 3. 如果选择了自定义匹配，保存HUB配置（每个HUB只允许一个zipcode）
            // 注意：验证已经在保存之前完成（第358行之后），这里只需要保存配置
            if (values.pickupHubMatchingType === 2 && savedChannelId) {
                // 传全量快照（含被清空为空串的项），后端据此逻辑删除这些HUB的配置。
                // 此处不能过滤空项：被清空的HUB一旦不出现在快照里，后端按「未出现即不变更」处理，
                // 清空就不会落库，表现为「保存没生效」
                const configs = Object.entries(hubZipcodeInputs)
                    .map(([hubId, zipcode]) => {
                        const trimmedZipcode = zipcode?.trim() || '';
                        return {
                            hubId: parseInt(hubId),
                            zipcodes: [trimmedZipcode] // 每个HUB最多一个zipcode，空串表示清空
                        };
                    });

                // 确保 savedChannelId 是有效的数字
                if (!savedChannelId || isNaN(savedChannelId)) {
                    console.error('Cannot save hub configs: savedChannelId is invalid:', savedChannelId);
                    throw new Error(String(t('central.channels.messages.saveChannelBaseInfoFailed')));
                }

                // 不再按 configs 是否为空短路：上方「至少配置一个」的校验已保证 configs 非空，
                // 该判断已无意义；且它会在 configs 为空时连带跳过 setCurrentChannelId / refetch
                // （所有验证已经在保存之前完成）
                await saveHubConfigs({
                    variables: {
                        channelId: savedChannelId,
                        configs: configs
                    }
                });

                // 如果是新建客户，先更新 currentChannelId，然后再 refetch
                // 直接传递 savedChannelId 给 refetch，避免 channelId 为 null 的问题
                if (mode === 'create' && savedChannelId) {
                    setCurrentChannelId(savedChannelId);
                    // Apollo 的 refetch 函数可以直接接受变量对象
                    // 使用类型断言来避免类型错误
                    refetchConfiguredHubs({ channelId: savedChannelId } as any);
                } else {
                    // 编辑客户时，直接 refetch（channelId 已经存在）
                    refetchConfiguredHubs();
                }
            }

            // 4. 显示成功消息
            // 如果是新建客户，更新 currentChannelId 和表单中的ID
            // 同时，如果之前有临时上传的文件，需要保存到数据库
            if (mode === 'create' && savedChannelId) {
                // 如果还没有更新 currentChannelId（比如没有保存交邮点配置），在这里更新
                if (!currentChannelId) {
                    setCurrentChannelId(savedChannelId);
                }
                // 更新表单中的ID为后端返回的真实ID
                const savedChannelIdString = savedChannel.data?.saveChannel?.id;
                if (savedChannelIdString) {
                    form.setFieldsValue({ id: savedChannelIdString });
                }

                // 注意：文件的上传和删除已经通过 onboardingDocumentsJson 一次性保存
                // 不需要单独调用 uploadDocument 和 deleteDocument mutation
                // 清空临时状态
                setPendingDocuments([]);
                setPendingDeletions(new Set());
                
                // 刷新文件列表（编辑模式下）
                if (mode !== 'create') {
                    refetchDocuments();
                }

                // 重新查询数据，以便显示最新的客户信息
                // 注意：需要等待 currentChannelId 更新后，channelId 才会更新
                // 使用 useEffect 来监听 channelId 的变化并触发 refetch
                // 这里先不调用 refetch，让 useEffect 来处理
            }

            // 重置初始化标记，以便下次打开时重新加载数据
            formInitializedRef.current = null;
            
            message.success(mode === 'create' ? t('central.channels.messages.channelCreatedSuccessfully') : t('central.channels.messages.channelUpdatedSuccessfully'));

            // 如果是新建客户，需要刷新列表，然后关闭抽屉
            if (mode === 'create') {
                // 重置 currentChannelId，确保下次打开新建时不会进入编辑模式
                setCurrentChannelId(null);
                // 重置表单，清除所有字段值（包括ID字段）
                form.resetFields();

                // 直接刷新客户列表，避免通过 onSubmit 导致重复保存
                await apolloClient.refetchQueries({
                    include: [GET_ALL_CHANNELS]
                });

                // 关闭抽屉
                onClose();
            } else {
                // 编辑客户时，保存成功后刷新数据缓存，确保下次打开时显示最新数据
                if (channelId) {
                    // 刷新客户完整信息的缓存，确保下次打开时从数据库获取最新数据
                    await apolloClient.refetchQueries({
                        include: [GET_COMPLETE_CHANNEL_INFO]
                    });
                }
                // 关闭抽屉
                onClose();
            }
        } catch (error: any) {
            console.error('Submit failed:', error);

            // 重置提交状态
            isSubmittingRef.current = false;
            setSubmitting(false);

            // 如果是用户取消操作，静默处理，不显示错误消息
            if (error?.message?.includes(t('central.channels.messages.submitCancelled')) || error?.message?.includes('submit') && error?.message?.includes('cancel')) {
                return; // 静默返回，不显示任何消息
            }

            // 如果是表单验证错误，显示具体的验证错误信息
            if (error?.errorFields && Array.isArray(error.errorFields)) {
                // 字段名到翻译键的映射
                const fieldNameMap: Record<string, string> = {
                    'channelName': 'central.channels.pageDetails.name',
                    'companyName': 'central.channels.pageDetails.companyName',
                    'isAutoGenerate': 'central.channels.pageDetails.autoGenerate',
                    'bdName': 'central.channels.basicInfo.bdName',
                    'englishShortName': 'central.channels.basicInfo.englishShortName',
                    'responsibleNode': 'central.channels.basicInfo.responsibleNode',
                    'customerType': 'central.channels.basicInfo.customerType',
                    'pickupHubMatchingType': 'central.channels.basicInfo.pickupHubMatching',
                    'settlementCustomerType': 'central.channels.settlement.settlementCustomerType',
                    'settlementTrackingPoint': 'central.channels.settlement.settlementTrackingPoint',
                    'billingCycleConfiguration': 'central.channels.settlement.billingCycleConfiguration',
                    'weightCalculationMode': 'central.channels.settlement.weightCalculationMode',
                    'settlementMode': 'central.channels.settlement.settlementMode',
                    'billType': 'central.channels.settlement.settlementBillType',
                    'timeZoneIana': 'central.channels.settlement.timeZone',
                    'billUnitType': 'central.channels.settlement.billUnitType',
                    'schemeLimitNode': 'central.channels.settlement.schemeLimitNode',
                    'dimDivisor': 'central.channels.settlement.dim',
                    'contactName': 'central.channels.pageDetails.contactName',
                    'contactPhone': 'central.channels.pageDetails.phone',
                    'financeContactName': 'central.channels.contact.financialContact',
                    'financeContactEmail': 'central.channels.contact.financialContactEmail',
                    'itContactName': 'central.channels.contact.itContact',
                    'itContactEmail': 'central.channels.contact.itContactEmail',
                    'forwardTimeValue': 'central.channels.fulfillment.forwardTime',
                    'forwardTimeDisplayUnit': 'central.channels.fulfillment.forwardTime',
                    'failureMethod': 'central.channels.fulfillment.failureMethod',
                    'storageTimeValue': 'central.channels.fulfillment.storageTime',
                    'storageTimeDisplayUnit': 'central.channels.fulfillment.storageTime',
                    'returnTimeValue': 'central.channels.fulfillment.returnTime',
                    'returnTimeDisplayUnit': 'central.channels.fulfillment.returnTime',
                    'trackDiscontinuationAssessmentType': 'central.channels.fulfillment.trackDiscontinuationAssessment',
                    'trackDiscontinuationAssessmentDays': 'central.channels.fulfillment.trackDiscontinuationAssessmentDays',
                };

                const errorMessages = error.errorFields.map((field: any) => {
                    const fieldKey = field.name?.[0] || '';
                    const fieldName = fieldNameMap[fieldKey]
                        ? String(t(fieldNameMap[fieldKey]))
                        : (fieldKey || String(t('common.unknown')));
                    const fieldError = field.errors?.[0] || String(t('central.channels.messages.validationFailed'));
                    return `${fieldName}: ${fieldError}`;
                });
                console.error('Validation errors:', errorMessages);
                // 显示第一个验证错误
                if (errorMessages.length > 0) {
                    message.error(errorMessages[0]);
                } else {
                    message.error(t('central.channels.messages.validationFailed'));
                }
            } else if (error?.message) {
                // 尝试国际化错误消息，如果无法国际化则直接显示
                message.error(error.message);
            } else {
                message.error(t('central.channels.messages.saveFailed'));
            }
        } finally {
            setSubmitting(false);
            isSubmittingRef.current = false;
        }
    };

    const handleFileUpload = async (file: RcFile) => {
        // 检查文件大小（20MB限制）
        const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
        if (file.size > MAX_FILE_SIZE) {
            const errorMessage = String(t('central.channels.messages.fileSizeExceeded', { maxSize: '20MB' }));
            message.error(errorMessage);
            return false;
        }

        setUploading(true);
        try {
            // 1. 上传文件到S3（这个操作不需要channelId）
            const { objectKey } = await fileUpload(file, 'onboarding-documents');

            // 新建和编辑模式都使用临时列表，保存时才真正保存到数据库
            // 这样可以保持一致性：上传和删除都是"待保存"状态
            setPendingDocuments(prev => [...prev, {
                objectKey: objectKey,
                filename: file.name,
                fileSize: file.size
            }]);
            message.success(t('central.channels.messages.fileUploadSuccess'));
        } catch (error: any) {
            message.error(t('central.channels.messages.fileUploadFailed') + ': ' + error.message);
        } finally {
            setUploading(false);
        }
        return false; // 阻止默认上传
    };

    const handleFileDelete = async (objectKey: string) => {
        if (mode === 'create') {
            // 新建客户时：从临时列表中删除
            setPendingDocuments(prev => prev.filter(doc => doc.objectKey !== objectKey));
            message.success(t('central.channels.messages.fileDeleteSuccess'));
        } else {
            // 编辑客户时：标记为待删除，保存时才真正删除
            setPendingDeletions(prev => new Set(prev).add(objectKey));
            message.success(t('central.channels.messages.fileDeleteSuccess'));
        }
    };

    const handleFileDownload = async (objectKey: string) => {
        const effectiveChannelId = mode === 'create' ? currentChannelId : channelId;
        if (!effectiveChannelId) {
            message.warning(t('central.channels.messages.pleaseSaveChannelInfoFirst'));
            return;
        }
        try {
            const { data } = await enqueueOnboardingDownload({
                variables: {
                    channelId: effectiveChannelId,
                    objectKey,
                },
            });
            const displayName = data?.enqueueChannelOnboardingDocumentDownload;
            if (displayName) {
                setOnboardingDownloadModalFileName(displayName);
                setOnboardingDownloadModalOpen(true);
            } else {
                message.error(t('central.channels.messages.enqueueOnboardingDownloadFailed'));
            }
        } catch (error: any) {
            message.error(
                `${t('central.channels.messages.enqueueOnboardingDownloadFailed')}: ${error?.message ?? ''}`.trim()
            );
        }
    };

    const handleOpenSendKeyModal = () => {
        setSendKeyModalVisible(true);
        setConfirmationChars(['', '', '', '']);
        setConfirmationInputs(['', '', '', '']);
        setSendKeyDisabled(true);
    };

    const handleConfirmationInputChange = (index: number, value: string) => {
        // 只更新临时输入值，不立即截断
        const newInputs = [...confirmationInputs];
        newInputs[index] = value;
        setConfirmationInputs(newInputs);
    };

    const handleConfirmationInputBlur = (index: number) => {
        // 失去焦点时，只保留最后一个字符
        const newChars = [...confirmationChars];
        const inputValue = confirmationInputs[index];
        newChars[index] = inputValue.length > 0 ? inputValue.slice(-1) : '';
        setConfirmationChars(newChars);

        // 同步更新显示值
        const newInputs = [...confirmationInputs];
        newInputs[index] = newChars[index];
        setConfirmationInputs(newInputs);

        // 检查是否4个输入框都有内容且组合起来是"确认发送"
        const fullText = newChars.join('');
        setSendKeyDisabled(fullText !== '确认发送');
    };

    const handleSendApiKey = async () => {
        // 防止重复点击
        if (sendingApiKey) {
            return;
        }

        if (!channelId) {
            message.error(t('central.channels.messages.channelIdNotExists'));
            return;
        }

        const fullText = confirmationChars.join('');
        if (fullText !== '确认发送') {
            message.error(t('central.channels.contact.confirmSendPlaceholder') as string);
            return;
        }

        // 检查IT联系人邮箱是否已设置（优先检查已保存的数据）
        const savedItContactEmail = completeInfo?.getCompleteChannelInfo?.channelContact?.itContactEmail;
        const formItContactEmail = form.getFieldValue('itContactEmail');

        // 如果数据库中没有保存的邮箱，提示用户先保存
        if (!savedItContactEmail || savedItContactEmail.trim() === '') {
            if (formItContactEmail && formItContactEmail.trim() !== '') {
                // 表单中有值但还没保存
                message.warning(t('central.channels.messages.pleaseSaveContactInfoFirst'));
            } else {
                // 表单中也没有值
                message.error(t('central.channels.messages.itContactEmailRequired'));
            }
            setSendKeyModalVisible(false);
            // 切换到联系人信息标签页
            setActiveTab('contact');
            return;
        }

        setSendingApiKey(true);
        try {
            await sendApiKey({
                variables: {
                    channelId: channelId,
                    confirmationText: '确认发送'
                }
            });
            message.success(t('central.channels.messages.apiKeySentSuccessfully'));
            setConfirmationChars(['', '', '', '']);
            setConfirmationInputs(['', '', '', '']);
            setSendKeyDisabled(true);
            setSendKeyModalVisible(false);
        } catch (error: any) {
            message.error(t('central.channels.messages.apiKeySendFailed') + ': ' + error.message);
        } finally {
            setSendingApiKey(false);
        }
    };

    const customerType = Form.useWatch('customerType', form);
    const orderQuantityLimit = Form.useWatch('orderQuantityLimit', form);
    const pickupHubMatchingType = Form.useWatch('pickupHubMatchingType', form);
    const weightCalculationMode = Form.useWatch('weightCalculationMode', form);
    const watchedContractingPartyId = Form.useWatch('contractingPartyId', form);

    const allHubs = allHubsData?.getAllAvailablePickupHubs || [];
    const configuredHubs = configuredHubsData?.getConfiguredPickupHubs || [];

    // 签约主体回显：仅当选项「已成功加载且非空」时，才判定当前值对应的主体是否已被删除（不在选项中），是则置空。
    // 加载中 / 查询失败 / 配置表暂无数据时，选项为空，此时不能误判为已删除，否则会清空已保存的签约主体导致回显丢失。
    useEffect(() => {
        if (!visible || contractingPartiesLoading || contractingPartyOptions.length === 0) {
            return;
        }
        if (watchedContractingPartyId == null) {
            return;
        }
        const exists = contractingPartyOptions.some(option => option.value === watchedContractingPartyId);
        if (!exists) {
            form.setFieldValue('contractingPartyId', undefined);
        }
    }, [visible, contractingPartiesLoading, contractingPartyOptions, watchedContractingPartyId, form]);

    // 初始化HUB邮编输入框的值（每个HUB只允许一个zipcode）
    // 交邮点输入框使用独立的 state，不受 Form 管理，需要单独跟踪初始化状态
    useEffect(() => {
        if (!visible) {
            return;
        }

        // 如果切换到揽收范围匹配（1），清空交邮点配置
        if (pickupHubMatchingType === 1) {
            setHubZipcodeInputs({});
            hubZipcodeInitializedRef.current = null; // 重置初始化标记，以便切换回自定义匹配时重新加载
            return;
        }

        // 新建客户时，如果选择了自定义匹配，初始化所有HUB为空字符串
        if (mode === 'create' && pickupHubMatchingType === 2 && allHubs.length > 0) {
            // 检查是否已经初始化过（避免重复初始化导致覆盖用户输入）
            const hasInitialized = Object.keys(hubZipcodeInputs).length > 0;
            if (!hasInitialized) {
                const inputs: Record<number, string> = {};
                allHubs.forEach(hub => {
                    if (hub.id) {
                        inputs[hub.id] = '';
                    }
                });
                setHubZipcodeInputs(inputs);
            }
            return;
        }

        // 编辑客户时，需要channelId才能初始化
        if (!channelId) {
            return;
        }

        // 等待 configuredHubs 数据加载完成
        if (configuredHubsLoading) {
            return;
        }

        const currentChannelId = channelId;
        // 只有当还未初始化或 channelId 变化时，才重新初始化
        if (hubZipcodeInitializedRef.current !== currentChannelId) {
            if (pickupHubMatchingType === 2 && configuredHubs.length > 0) {
                const inputs: Record<number, string> = {};
                configuredHubs.forEach(hub => {
                    // 只取第一个zipcode（如果存在多个，只显示第一个）
                    inputs[hub.hubId] = hub.zipcodes.length > 0 ? hub.zipcodes[0] : '';
                });
                setHubZipcodeInputs(inputs);
                hubZipcodeInitializedRef.current = currentChannelId;
            } else if (pickupHubMatchingType === 2 && allHubs.length > 0 && configuredHubs.length === 0) {
                // 如果没有配置，初始化所有HUB为空字符串
                const inputs: Record<number, string> = {};
                allHubs.forEach(hub => {
                    if (hub.id) {
                        inputs[hub.id] = '';
                    }
                });
                setHubZipcodeInputs(inputs);
                hubZipcodeInitializedRef.current = currentChannelId;
            }
        }
    }, [visible, channelId, mode, pickupHubMatchingType, configuredHubs, configuredHubsLoading, allHubs]);
    // 显示的文件列表：
    // - 新建客户时：只显示临时上传的文件
    // - 编辑客户时：显示数据库中的文件（过滤掉待删除的文件）+ 临时上传的文件（待保存）
    const documents = mode === 'create' 
        ? pendingDocuments 
        : [
            ...(documentsData?.getOnboardingDocuments || []).filter(doc => !pendingDeletions.has(doc.objectKey)),
            ...pendingDocuments
          ];

    const canAddMoreFinanceContacts = financeContacts.length < 10;

    const tabItems: TabsProps['items'] = [
        {
            key: 'basic',
            label: t('central.channels.tabs.basic'),
            forceRender: true, // 强制渲染，确保字段总是被注册到 Form 中
            children: (
                <div className="space-y-4">
                    <Form.Item name="id" label={t('central.channels.pageDetails.id')}>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item name="channelName" label={t('central.channels.pageDetails.name')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="companyName" label={t('central.channels.pageDetails.companyName')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="companyAddress" label={t('central.channels.pageDetails.companyAddress')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="taxRate" label={t('central.channels.pageDetails.taxRate')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="taxId" label={t('central.channels.pageDetails.taxId')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="isAutoGenerate" label={t('central.channels.pageDetails.autoGenerate')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: false, label: t('common.no') },
                            { value: true, label: t('common.yes') }
                        ]} />
                    </Form.Item>
                    <Form.Item name="allowQueryAbnormalWeighingInfo" label="允许查询异常称重信息" rules={[{ required: true }]}>
                        <Select options={[
                            { value: false, label: t('common.no') },
                            { value: true, label: t('common.yes') }
                        ]} />
                    </Form.Item>
                    <Form.Item
                        name="status"
                        label={t('central.channels.pageDetails.status')}
                        rules={[{ required: true, message: t('central.channels.messages.pleaseSelectStatus') as string }]}
                    >
                        <Select
                            options={[
                                { value: 1, label: t('enum.activeStatus.active') },
                                { value: 0, label: t('enum.activeStatus.suspended') },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item name="contractingPartyId" label={t('central.channels.pageDetails.contractingParty')}>
                        <Select
                            placeholder={String(t('central.channels.pageDetails.contractingPartyPlaceholder'))}
                            options={contractingPartyOptions}
                            loading={contractingPartiesLoading}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item
                        name="bdName"
                        label={t('central.channels.basicInfo.bdName')}
                        rules={[
                            { required: true, message: t('central.channels.basicInfo.bdName') + ' ' + t('common.isRequired') },
                            { max: 30, message: t('central.channels.basicInfo.bdName') + ' ' + t('common.max30Characters') },
                            {
                                validator: (_, value) => {
                                    if (!value || value.trim() === '') {
                                        return Promise.resolve();
                                    }
                                    // 只允许中文字符、英文字母和空格
                                    const pattern = /^[\u4e00-\u9fa5a-zA-Z\s]+$/;
                                    if (!pattern.test(value)) {
                                        return Promise.reject(new Error(t('central.channels.basicInfo.bdName') + ' ' + t('common.onlyChineseEnglishSpace')));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input
                            maxLength={30}
                            onChange={(e) => {
                                // 过滤掉非中英文和空格的字符
                                const value = e.target.value;
                                const filteredValue = value.replace(/[^\u4e00-\u9fa5a-zA-Z\s]/g, '');
                                if (filteredValue !== value) {
                                    // 使用 setTimeout 确保在下一个事件循环中更新，避免与 React 的状态更新冲突
                                    setTimeout(() => {
                                        form.setFieldsValue({ bdName: filteredValue });
                                    }, 0);
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="englishShortName"
                        label={
                            <span>
                                {t('central.channels.basicInfo.englishShortName')}
                                <Tooltip title={t('central.channels.basicInfo.englishShortNameTooltip')}>
                                    <QuestionCircleOutlined className="ml-2 text-gray-400 cursor-help" />
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: t('central.channels.basicInfo.englishShortName') + ' ' + t('common.isRequired') },
                            {
                                validator: (_, value) => {
                                    // required 规则已经处理了空值情况，这里只检查格式和长度
                                    if (!value || value.trim() === '') {
                                        return Promise.resolve();
                                    }
                                    // 检查长度
                                    if (value.length > 8) {
                                        return Promise.reject(new Error(t('central.channels.basicInfo.englishShortName') + ' ' + t('common.max8Characters')));
                                    }
                                    // 只允许英文字母和数字
                                    const pattern = /^[a-zA-Z0-9]+$/;
                                    if (!pattern.test(value)) {
                                        return Promise.reject(new Error(t('central.channels.basicInfo.englishShortName') + ' ' + t('common.onlyEnglishAndNumbers')));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input
                            maxLength={8}
                            onChange={(e) => {
                                // 过滤掉非英文和数字的字符（maxLength 已经限制了长度）
                                const value = e.target.value;
                                const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
                                if (filteredValue !== value) {
                                    // 使用 setTimeout 确保在下一个事件循环中更新，避免与 React 的状态更新冲突
                                    setTimeout(() => {
                                        form.setFieldsValue({ englishShortName: filteredValue });
                                    }, 0);
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item label={t('central.channels.basicInfo.onboardingNote')} required>
                        {canUploadFile && (
                        <Upload
                            beforeUpload={handleFileUpload}
                            fileList={[]}
                            maxCount={3}
                            accept=".jpg,.doc,.docx,.xls,.xlsx,.pdf,.bmp,.txt"
                            disabled={uploading}
                        >
                            <Button
                                type="link"
                                disabled={uploading || documents.length >= 3}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                {t('central.channels.basicInfo.uploadFile')}
                            </Button>
                        </Upload>
                        )}
                        <div className="mt-2">
                            {documents.map((doc: any) => (
                                <div key={doc.objectKey} className="flex items-center justify-between p-2 border rounded">
                                    <span>{doc.filename}</span>
                                    <div className="flex gap-2">
                                        {canDownloadOnboardingDocuments && (
                                        <Button
                                            type="link"
                                            icon={<DownloadOutlined />}
                                            loading={enqueueOnboardingDownloadLoading}
                                            onClick={() => handleFileDownload(doc.objectKey)}
                                        >
                                            {t('components.button.download')}
                                        </Button>
                                        )}
                                        {canDeleteFile && (
                                        <Button
                                            type="link"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleFileDelete(doc.objectKey)}
                                        >
                                            {t('central.channels.basicInfo.delete')}
                                        </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Form.Item>
                    <Form.Item name="responsibleNode" label={t('central.channels.basicInfo.responsibleNode')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 1, label: t('central.channels.basicInfo.responsibleNodeOptions.ascan') },
                            { value: 2, label: t('central.channels.basicInfo.responsibleNodeOptions.deliverReturned') }
                        ]} />
                    </Form.Item>
                    <Form.Item name="customerType" label={t('central.channels.basicInfo.customerType')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 1, label: t('central.channels.basicInfo.customerTypeOptions.crossBorderDedicatedLine') },
                            { value: 2, label: t('central.channels.basicInfo.customerTypeOptions.localPickup') },
                            { value: 3, label: t('central.channels.basicInfo.customerTypeOptions.localCustomer') },
                            { value: 4, label: t('central.channels.basicInfo.customerTypeOptions.smb') }
                        ]} />
                    </Form.Item>
                    {customerType === 1 && (
                        <>
                            <Form.Item name="pickupHubMatchingType" label={t('central.channels.basicInfo.pickupHubMatching')} rules={[{ required: true }]} initialValue={1}>
                                <Select
                                    options={[
                                        { value: 1, label: t('central.channels.basicInfo.pickupHubMatchingOptions.hubPickupAreaMatch') },
                                        { value: 2, label: t('central.channels.basicInfo.pickupHubMatchingOptions.customerCustomMatch') },
                                        { value: 3, label: t('central.channels.basicInfo.pickupHubMatchingOptions.portFieldMatch') },
                                        { value: 4, label: t('central.channels.basicInfo.pickupHubMatchingOptions.fixedHubMatch') }
                                    ]}
                                    onChange={(value) => {
                                        // 切换枚举值时清理"固定HUB"输入，避免脏数据被一同提交
                                        if (value !== 4) {
                                            form.setFieldValue('fixedPickupHubId', undefined);
                                        }
                                    }}
                                />
                            </Form.Item>
                            {pickupHubMatchingType === 4 && (
                                <Form.Item
                                    name="fixedPickupHubId"
                                    label={t('central.channels.basicInfo.fixedPickupHub')}
                                    rules={[{ required: true, message: String(t('central.channels.basicInfo.fixedPickupHubRequired')) }]}
                                >
                                    <Select
                                        placeholder={String(t('central.channels.basicInfo.fixedPickupHubPlaceholder'))}
                                        options={allHubs
                                            .filter(hub => hub.id != null)
                                            .map(hub => ({
                                                value: hub.id as number,
                                                label: hub.name ?? `HUB${hub.id}`
                                            }))}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            )}
                            {pickupHubMatchingType === 2 && (
                                <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
                                    <div className="border rounded p-4 bg-gray-50 space-y-3">
                                        <div className="text-gray-500 text-xs">
                                            {t('central.channels.basicInfo.pickupPointOptionalHint')}
                                        </div>
                                        {allHubs.map((hub) => {
                                            if (!hub.id) return null;
                                            // 从name中提取HUB代码，如果name包含空格，取第一部分；否则使用name
                                            const hubCode = hub.name?.split(' ')[0]?.toUpperCase() || `HUB${hub.id}`;
                                            return (
                                                <div key={hub.id} className="flex items-center justify-between gap-4">
                                                    <div className="flex-shrink-0">
                                                        <span className="font-semibold whitespace-nowrap">HUB: {hubCode}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        <span className="whitespace-nowrap">{t('central.channels.basicInfo.pickupPoint')}:</span>
                                                        <div className="w-[250px]">
                                                        <Input
                                                            placeholder={String(t('central.channels.basicInfo.pickupPointPlaceholder'))}
                                                            value={hubZipcodeInputs[hub.id] || ''}
                                                            onChange={(e) => {
                                                                // 只允许输入数字，最多5位
                                                                const value = e.target.value.replace(/\D/g, '').slice(0, 5);

                                                                // 检查是否与其他HUB的zipcode重复
                                                                const otherZipcodes = Object.entries(hubZipcodeInputs)
                                                                    .filter(([otherHubId, _]) => parseInt(otherHubId) !== hub.id)
                                                                    .map(([_, zipcode]) => zipcode.trim())
                                                                    .filter(z => z.length === 5);

                                                                if (value.length === 5 && otherZipcodes.includes(value)) {
                                                                    message.error(t('central.channels.messages.pickupPointCannotDuplicate'));
                                                                    return;
                                                                }

                                                                setHubZipcodeInputs({
                                                                    ...hubZipcodeInputs,
                                                                    [hub.id!]: value
                                                                });
                                                            }}
                                                            onBlur={async (e) => {
                                                                // 检查是否是因为点击提交按钮导致的失去焦点
                                                                // 如果是，跳过验证，由提交逻辑统一处理
                                                                const relatedTarget = e.relatedTarget as HTMLElement;
                                                                if (relatedTarget) {
                                                                    // 检查是否点击了提交按钮或其父元素
                                                                    const isSubmitButton = relatedTarget.closest('button[type="submit"]') !== null ||
                                                                                          relatedTarget.textContent?.includes('确认') ||
                                                                                          relatedTarget.closest('.ant-drawer-footer') !== null;
                                                                    if (isSubmitButton) {
                                                                        return; // 跳过验证，由提交逻辑处理
                                                                    }
                                                                }

                                                                const zipcode = e.target.value.trim();
                                                                // 只有当zipcode是5位数字时才验证
                                                                if (zipcode.length === 5 && /^\d{5}$/.test(zipcode)) {
                                                                    try {
                                                                        // 1. 先检查是否在系统基础配置中
                                                                        const { data: validateData } = await validateZipcodesExist({
                                                                            variables: { zipcodes: [zipcode] }
                                                                        });

                                                                        const invalidZipcodes = validateData?.validateZipcodesExist || [];
                                                                        if (invalidZipcodes.length > 0) {
                                                                            message.error(t('central.channels.messages.zipcodeNotInSystemConfig'));
                                                                            // 清除无效的zipcode
                                                                            setHubZipcodeInputs(prev => ({
                                                                                ...prev,
                                                                                [hub.id!]: ''
                                                                            }));
                                                                            return;
                                                                        }

                                                                        // 2. 检查是否在任何hub的揽收/可派送区域内
                                                                        const { data: hubAreaData } = await isZipcodeInHubAreas({
                                                                            variables: { zipcode }
                                                                        });

                                                                        if (hubAreaData?.isZipcodeInHubAreas === false) {
                                                                            // 不在任何hub的揽收/可派送区域内，弹出确认框
                                                                            Modal.confirm({
                                                                                title: t('central.channels.messages.confirmTitle'),
                                                                                content: t('central.channels.messages.zipcodeNotInHubAreasSingle', { zipcode }),
                                                                                okText: t('components.button.confirm'),
                                                                                cancelText: t('components.button.cancel'),
                                                                                onOk: () => {
                                                                                    // 用户确认，保留zipcode
                                                                                },
                                                                                onCancel: () => {
                                                                                    // 用户取消，清除zipcode
                                                                                    setHubZipcodeInputs(prev => ({
                                                                                        ...prev,
                                                                                        [hub.id!]: ''
                                                                                    }));
                                                                                }
                                                                            });
                                                                        }
                                                                    } catch (error: any) {
                                                                        // 验证失败时不显示错误，避免干扰用户输入
                                                                        console.error('验证zipcode失败:', error);
                                                                    }
                                                                }
                                                            }}
                                                            maxLength={5}
                                                        />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Form.Item>
                            )}
                        </>
                    )}
                    <Form.Item name="remark" label={t('central.channels.pageDetails.remark')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </div>
            )
        },
        ...(canManageSettlement ? [{
            key: 'settlement',
            label: t('central.channels.tabs.settlement'),
            forceRender: true, // 强制渲染，确保字段总是被注册到 Form 中
            children: (
                <div className="space-y-4">
                    <Form.Item name="settlementMode" label={t('central.channels.settlement.settlementMode')} rules={[{ required: true }]}>
                        <Select
                                options={[
                                { value: 0, label: 'PAY_LATER_BILL' },
{ value: 1, label: 'PAY_REAL_TIME' }
                            ]}
                            onChange={(value) => {
                                setSettlementMode(value);
                                // 如果切换到 PAY_REAL_TIME，清空订单量限制相关字段
                                if (value === 1) {
                                    form.setFieldsValue({
                                        orderQuantityLimit: undefined,
                                        orderDailyLimit: undefined,
                                        orderCycleLimit: undefined
                                    });
                                }
                            }}
                         />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.settlementMode !== currentValues.settlementMode
                        }
                    >
                        {({ getFieldValue }) => {
                            const currentSettlementMode = getFieldValue('settlementMode');
                            const isPayRealtime = currentSettlementMode === 1;
                            return (
                                <>
                                    {!isPayRealtime && (
                                        <Form.Item name="settlementCustomerType" label={t('central.channels.settlement.settlementCustomerType')} rules={[{ required: true }]}>
                                            <Select options={[
                                                { value: 2, label: 'standard' },
                                                { value: 0, label: 'temu' }
                                            ]} />
                                        </Form.Item>
                                    )}
                                    {!isPayRealtime && (
                                        <Form.Item name="settlementTrackingPoint" label={t('central.channels.settlement.settlementTrackingPoint')} rules={[{ required: true }]}>
                                            <Select
                                                options={settlementTrackingPointOptions}
                                                loading={trackingPointsLoading}
                                                placeholder="Please select settlement tracking point"
                                            />
                                        </Form.Item>
                                    )}
                                    {!isPayRealtime && (
                                        <Form.Item name="billingCycleConfiguration" label={t('central.channels.settlement.billingCycleConfiguration')} rules={[{ required: true }]}>
                                            <Select options={[
                                                { value: 1, label: 'NATURE_WEEK' },
                                                { value: 2, label: 'NATURE_MONTH' },
                                                { value: 3, label: 'HALF_MONTH' }
                                            ]} />
                                        </Form.Item>
                                    )}
                                </>
                            );
                        }}
                    </Form.Item>

                    <Form.Item name="weightCalculationMode" label={t('central.channels.settlement.weightCalculationMode')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 0, label: 'Max(Client Weight,Client volumetric weight)' },
                            { value: 1, label: 'Max(Actual weight,Actual volumetric weight)' },
                            { value: 2, label: 'Max(Actual weight,Actual volumetric weight,Client Weight)/1bs' },
                            { value: 3, label: 'Actual weight'},
                            { value: 4, label: 'Max(Actual weight,Actual volumetric weight,Client Weight)/Xbs'},
                            { value: 5, label: 'Client Weight' },
                            { value: 6, label: 'Max(Client weight，Client volumetric weight)/Xlb' },
                        ]} />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.weightCalculationMode !== currentValues.weightCalculationMode
                        }
                    >
                        {({ getFieldValue }) => {
                            const weightCalculationMode = getFieldValue('weightCalculationMode');
                            if (weightCalculationMode === 4 || weightCalculationMode === 6) {
                                return (
                                    <Form.Item
                                        name="weightCalculationThresholdX"
                                        label="Threshold X (lbs)"
                                        initialValue={1}
                                        normalize={(value) => {
                                            if (value === undefined || value === null || value === '') {
                                                return undefined;
                                            }
                                            const num = Number(value);
                                            return isNaN(num) ? undefined : num;
                                        }}
                                        rules={[
                                            { required: true, message: 'Please input Weight Calculation Threshold X!' },
                                            {
                                                validator: (_, value) => {
                                                    const num = Number(value);
                                                    if (isNaN(num) || !Number.isInteger(num) || num <= 0) {
                                                        return Promise.reject(new Error('Weight Calculation Threshold X must be a positive integer!'));
                                                    }
                                                    if (num > 99) {
                                                        return Promise.reject(new Error('Weight Calculation Threshold X must be at most 99!'));
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <Input
                                            type="number"
                                            placeholder="1"
                                            min={1}
                                            max={99}
                                            step={1}
                                        />
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.settlementMode !== currentValues.settlementMode
                        }
                    >
                        {({ getFieldValue }) => {
                            const currentSettlementMode = getFieldValue('settlementMode');
                            const isPayRealtime = currentSettlementMode === 1;
                            if (!isPayRealtime) {
                                return (
                                    <Form.Item name="billType" label={t('central.channels.settlement.settlementBillType')} rules={[{ required: true }]}>
                                        <Select
                                            mode="multiple"
                                            options={[
                                                { value: 0, label: 'DELIVERY_FEE_BILL' },
                                                { value: 1, label: 'SLA_BILL' },
                                                { value: 3, label: 'SORTING_BILL' },
                                            ]}
                                        />
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Form.Item
                        name="timeZoneIana"
                        label={t('central.channels.settlement.timeZone')}
                        rules={[{ required: true }]}
                        extra={isTimeZoneLocked ? (
                            <span className="text-amber-600 text-xs">
                                {t('central.channels.settlement.timeZoneLockedHint')}
                            </span>
                        ) : undefined}
                    >
                        <Select
                            disabled={isTimeZoneLocked}
                            options={[
                                { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
                                { value: 'America/Los_Angeles', label: 'America/Los_Angeles' }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item name="billUnitType" label={t('central.channels.settlement.billUnitType')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 0, label: 'English' },
                            { value: 1, label: 'Metric' },
                        ]}
                        disabled={true}
                        />
                    </Form.Item>
                    <Form.Item name="schemeLimitNode" label={t('central.channels.settlement.schemeLimitNode')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 'Created', label: 'CREATED_NODE' },
                            { value: 'PickupArrivalScan', label: 'PICKUP_ARRIVAL_SCAN_NODE' }
                        ]} />
                    </Form.Item>
                    <Form.Item
                        name="netDay"
                        label={t('central.channels.settlement.netDay')}
                        required
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.reject(new Error(t('central.channels.settlement.netDay') + ' ' + t('common.isRequired')));
                                    }
                                    const num = Number(value);
                                    if (isNaN(num) || !Number.isInteger(num) || num < 1) {
                                        return Promise.reject(new Error(t('central.channels.settlement.netDay') + ' ' + t('common.mustBePositiveInteger')));
                                    }
                                    const str = num.toString();
                                    if (str.length > 3) {
                                        return Promise.reject(new Error(t('central.channels.settlement.netDay') + ' ' + t('common.max3Characters')));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <InputNumber
                            min={1}
                            max={999}
                            precision={0}
                            style={{ width: '100%' }}
                            addonAfter={t('common.day')}
                        />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.settlementMode !== currentValues.settlementMode
                        }
                    >
                        {({ getFieldValue }) => {
                            const settlementMode = getFieldValue('settlementMode');
                            if (settlementMode === 0) {
                                return (
                                    <Form.Item
                                        name="orderQuantityLimit"
                                        label={t('central.channels.settlement.orderQuantityLimit')}
                                        initialValue={1}
                                        required
                                        rules={[{ required: true }]}
                                    >
                                        <Select options={[
                                            { value: 1, label: t('central.channels.settlement.orderQuantityLimitOptions.limit') },
                                            { value: 2, label: t('central.channels.settlement.orderQuantityLimitOptions.noLimit') }
                                        ]} />
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.settlementMode !== currentValues.settlementMode ||
                            prevValues.orderQuantityLimit !== currentValues.orderQuantityLimit
                        }
                    >
                        {({ getFieldValue }) => {
                            const settlementMode = getFieldValue('settlementMode');
                            const orderQuantityLimit = getFieldValue('orderQuantityLimit');
                            // 只有在 PAY_LATER_BILL (0) 且选择了限制 (1) 时才显示
                            if (settlementMode === 0 && orderQuantityLimit === 1) {
                                return (
                                    <>
                                        <Form.Item
                                            name="orderDailyLimit"
                                            label={t('central.channels.settlement.orderDailyLimit')}
                                            required
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (value === undefined || value === null || value === '') {
                                                            return Promise.reject(new Error(t('central.channels.settlement.orderDailyLimit') + ' ' + t('common.isRequired')));
                                                        }
                                                        const num = Number(value);
                                                        if (isNaN(num) || !Number.isInteger(num) || num < 1) {
                                                            return Promise.reject(new Error(t('central.channels.settlement.orderDailyLimit') + ' ' + t('common.mustBePositiveInteger')));
                                                        }
                                                        const str = num.toString();
                                                        if (str.length > 10) {
                                                            return Promise.reject(new Error(t('central.channels.settlement.orderDailyLimit') + ' ' + t('common.max10Characters')));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <InputNumber
                                                min={1}
                                                max={9999999999}
                                                precision={0}
                                                style={{ width: '100%' }}
                                                parser={(value) => {
                                                    if (!value) return '';
                                                    // 只保留数字
                                                    const digits = value.replace(/\D/g, '');
                                                    // 如果超过10位，显示提示并截断
                                                    if (digits.length > 10) {
                                                        message.warning(t('central.channels.settlement.orderDailyLimit') + ' ' + t('common.max10Characters'));
                                                        return digits.slice(0, 10) as any;
                                                    }
                                                    return digits as any;
                                                }}
                                                formatter={(value) => {
                                                    if (!value) return '';
                                                    return value.toString().replace(/\D/g, '').slice(0, 10) as any;
                                                }}
                                            />
                            </Form.Item>
                                        <Form.Item
                                            name="orderCycleLimit"
                                            label={t('central.channels.settlement.orderCycleLimit')}
                                            required
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (value === undefined || value === null || value === '') {
                                                            return Promise.reject(new Error(t('central.channels.settlement.orderCycleLimit') + ' ' + t('common.isRequired')));
                                                        }
                                                        const num = Number(value);
                                                        if (isNaN(num) || !Number.isInteger(num) || num < 1) {
                                                            return Promise.reject(new Error(t('central.channels.settlement.orderCycleLimit') + ' ' + t('common.mustBePositiveInteger')));
                                                        }
                                                        const str = num.toString();
                                                        if (str.length > 10) {
                                                            return Promise.reject(new Error(t('central.channels.settlement.orderCycleLimit') + ' ' + t('common.max10Characters')));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <InputNumber
                                                min={1}
                                                max={9999999999}
                                                precision={0}
                                                style={{ width: '100%' }}
                                                parser={(value) => {
                                                    if (!value) return '';
                                                    // 只保留数字
                                                    const digits = value.replace(/\D/g, '');
                                                    // 如果超过10位，显示提示并截断
                                                    if (digits.length > 10) {
                                                        message.warning(t('central.channels.settlement.orderCycleLimit') + ' ' + t('common.max10Characters'));
                                                        return digits.slice(0, 10) as any;
                                                    }
                                                    return digits as any;
                                                }}
                                                formatter={(value) => {
                                                    if (!value) return '';
                                                    return value.toString().replace(/\D/g, '').slice(0, 10) as any;
                                                }}
                                            />
                            </Form.Item>
                        </>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Form.Item
                        name="gri"
                        label={t('central.channels.settlement.gri')}
                        required
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.reject(new Error(t('central.channels.settlement.gri') + ' ' + t('common.isRequired')));
                                    }
                                    const num = Number(value);
                                    if (isNaN(num)) {
                                        return Promise.reject(new Error(t('central.channels.settlement.gri') + ' ' + t('common.mustBeBetween0And100')));
                                    }
                                    if (num < 0 || num > 100) {
                                        return Promise.reject(new Error(t('central.channels.settlement.gri') + ' ' + t('common.mustBeBetween0And100')));
                                    }
                                    // 检查小数位数是否超过2位
                                    const str = value.toString();
                                    const decimalIndex = str.indexOf('.');
                                    if (decimalIndex !== -1) {
                                        const decimalPart = str.substring(decimalIndex + 1);
                                        if (decimalPart.length > 2) {
                                            return Promise.reject(new Error(t('central.channels.settlement.gri') + ' ' + t('common.max2DecimalPlaces')));
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            precision={2}
                            style={{ width: '100%' }}
                            step={0.01}
                            addonAfter="%"
                        />
                    </Form.Item>
                    <Form.Item name="dimDivisor" label={t('central.channels.settlement.dim')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 139, label: '139-5000' },
                            { value: 166, label: '166-6000' },
                            { value: 194, label: '194-7000' },
                        ]} />
                    </Form.Item>
                    {mode === 'edit' && channel?.id && (
                        <Form.Item label={t('central.channels.settlement.priceConfigure')}>
                            <Button type="primary" onClick={() => navigate(`/settlement/price-center#tab=setup&clientId=${channel.id}`)}>
                                {t('central.channels.settlement.viewPricingScheme')}
                            </Button>
                        </Form.Item>
                    )}
                </div>
            )
        }] : []),
        {
            key: 'contact',
            label: t('central.channels.tabs.contact'),
            forceRender: true, // 强制渲染，确保字段总是被注册到 Form 中
            children: (
                <div className="space-y-4">
                    <Form.Item name="contactName" label={t('central.channels.pageDetails.contactName')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="contactPhone" label={t('central.channels.pageDetails.phone')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="email" label={t('central.channels.pageDetails.email')} rules={[{ type: 'email' }]}>
                        <Input />
                    </Form.Item>

                    {/* 财务联系人列表（1~10组，纵向排布） */}
                    <div className="border rounded p-4 bg-gray-50 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="font-semibold">{t('central.channels.contact.financialContact')}</span>
                            {canAddFinanceContact && (
                            <Button
                                type="link"
                                onClick={() => {
                                    if (!canAddMoreFinanceContacts) {
                                        message.error(t('central.channels.messages.maxFinanceContacts') as string);
                                        return;
                                    }
                                    setFinanceContacts([...financeContacts, { name: '', email: '' }]);
                                }}
                                style={
                                    canAddMoreFinanceContacts
                                        ? undefined
                                        : { color: '#d9d9d9', cursor: 'not-allowed' }
                                }
                            >
                                {t('central.channels.contact.addFinanceContact')}
                            </Button>
                            )}
                        </div>
                        {financeContacts.map((fc, index) => (
                            <div
                                key={index}
                                className="border border-dashed border-gray-300 rounded-md p-3 space-y-3 bg-white"
                            >
                                <Form.Item
                                    label={t('central.channels.contact.financialContact')}
                                    colon={false}
                                >
                                    <Input
                                        maxLength={50}
                                        value={fc.name}
                                        onChange={(e) => {
                                            const next = [...financeContacts];
                                            next[index] = { ...next[index], name: e.target.value };
                                            setFinanceContacts(next);
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label={t('central.channels.contact.financialContactEmail')}
                                    colon={false}
                                >
                                    <Input
                                        value={fc.email}
                                        onChange={(e) => {
                                            const next = [...financeContacts];
                                            next[index] = { ...next[index], email: e.target.value };
                                            setFinanceContacts(next);
                                        }}
                                    />
                                </Form.Item>
                                {financeContacts.length > 1 && canDeleteFinanceContact && (
                                    <div className="text-right">
                                        <Button
                                            danger
                                            type="link"
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: t('central.channels.contact.deleteFinanceContactConfirm'),
                                                    onOk: () => {
                                                        setFinanceContacts(prev =>
                                                            prev.filter((_, i) => i !== index)
                                                        );
                                                    }
                                                });
                                            }}
                                        >
                                            {t('central.channels.contact.removeFinanceContact')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <Form.Item name="itContactName" label={t('central.channels.contact.itContact')} rules={[{ required: true, max: 50 }]}>
                        <Input maxLength={50} />
                    </Form.Item>
                    <Form.Item name="itContactEmail" label={t('central.channels.contact.itContactEmail')} rules={[{ required: true, type: 'email' }]}>
                        <Input />
                    </Form.Item>
                    {/*
                      * 只读模式用 Form disabled 统一禁用字段，但发送 Key / 账号管理 / 日志查询是动作按钮而非表单字段，
                      * 客服（只读 + 可管运营账号）仍需可点，这里用 ConfigProvider 退出 Form 的禁用上下文
                      */}
                    {channelId && canSendKey && (
                        <Form.Item label={t('central.channels.contact.sendKeySecret')}>
                            <ConfigProvider componentDisabled={false}>
                                <Button
                                    type="primary"
                                    onClick={handleOpenSendKeyModal}
                                >
                                    {t('central.channels.contact.clickToSend')}
                                </Button>
                            </ConfigProvider>
                        </Form.Item>
                    )}
                    {channelId && (canManageCustomerAccounts || canViewCustomerLogs) && (
                        <div className="border-t border-gray-200 pt-4 mt-4">
                            <ConfigProvider componentDisabled={false}>
                            <Space>
                                {canManageCustomerAccounts && (
                                <Button
                                    icon={<UserAddOutlined />}
                                    onClick={() => setAccountModalVisible(true)}
                                >
                                    {t('central.channels.crm.generateAccount')}
                                </Button>
                                )}
                                {canViewCustomerLogs && (
                                <Button
                                    icon={<FileSearchOutlined />}
                                    onClick={() => setLogModalVisible(true)}
                                >
                                    {t('central.channels.crm.viewLogs')}
                                </Button>
                                )}
                            </Space>
                            </ConfigProvider>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'fulfillment',
            label: t('central.channels.tabs.fulfillment'),
            forceRender: true,
            children: (
                <div className="space-y-4">
                    <Form.Item
                        label={t('central.channels.fulfillment.forwardTime')}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        required
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="forwardTimeValue" noStyle rules={[{ required: true, message: String(t('common.isRequired')) }]}>
                                <InputNumber
                                    min={1}
                                    precision={0}
                                    style={{ width: '50%' }}
                                />
                            </Form.Item>
                            <Form.Item name="forwardTimeDisplayUnit" noStyle initialValue={2}>
                                <Select
                                    style={{ width: '50%' }}
                                    options={[
                                        { value: 1, label: t('common.hour') },
                                        { value: 2, label: t('common.day') }
                                    ]}
                                />
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item
                        name="failureMethod"
                        label={t('central.channels.fulfillment.failureMethod')}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        rules={[{ required: true, message: String(t('common.isRequired')) }]}
                    >
                        <Select
                            placeholder={String(t('common.pleaseSelect'))}
                            options={[
                                { value: 1, label: t('central.channels.fulfillment.returnToCustomer') },
                                { value: 2, label: t('central.channels.fulfillment.destroy') },
                                { value: 3, label: t('central.channels.fulfillment.byOrderLevel') }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.failureMethod !== currentValues.failureMethod}
                    >
                        {({ getFieldValue }) => {
                            const failureMethod = getFieldValue('failureMethod');
                            // 仓内暂存时长：销毁（2）必填；随订单维度（3）也必填
                            const renderStorageTime = (required: boolean) => (
                                <Form.Item
                                    label={t('central.channels.fulfillment.storageTime')}
                                    required={required}
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Form.Item name="storageTimeValue" noStyle rules={required ? [{ required: true, message: String(t('common.isRequired')) }] : undefined}>
                                            <InputNumber
                                                min={1}
                                                precision={0}
                                                style={{ width: '50%' }}
                                            />
                                        </Form.Item>
                                        <Form.Item name="storageTimeDisplayUnit" noStyle initialValue={2}>
                                            <Select
                                                style={{ width: '50%' }}
                                                options={[
                                                    { value: 1, label: t('common.hour') },
                                                    { value: 2, label: t('common.day') }
                                                ]}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            );
                            // 逆向履约闭环时效要求：退回客户仓（1）可选；随订单维度（3）必填
                            const renderReturnTime = (required: boolean) => (
                                <Form.Item
                                    label={t('central.channels.fulfillment.returnTime')}
                                    required={required}
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Form.Item name="returnTimeValue" noStyle rules={required ? [{ required: true, message: String(t('common.isRequired')) }] : undefined}>
                                            <InputNumber
                                                min={1}
                                                precision={0}
                                                style={{ width: '50%' }}
                                            />
                                        </Form.Item>
                                        <Form.Item name="returnTimeDisplayUnit" noStyle initialValue={2}>
                                            <Select
                                                style={{ width: '50%' }}
                                                options={[
                                                    { value: 1, label: t('common.hour') },
                                                    { value: 2, label: t('common.day') }
                                                ]}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            );
                            if (failureMethod === 2) {
                                // 销毁：显示仓内暂存时长（必填）
                                return renderStorageTime(true);
                            } else if (failureMethod === 1) {
                                // 退回客户仓：显示逆向履约闭环时效要求（可选）
                                return renderReturnTime(false);
                            } else if (failureMethod === 3) {
                                // 随订单维度：两个时效字段同时显示且均必填
                                // 后续按订单级 exceptionHandleType 决定走哪条路径，配置阶段无法预知，故都必须有值
                                return (
                                    <>
                                        {renderStorageTime(true)}
                                        {renderReturnTime(true)}
                                    </>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Form.Item
                        name="trackDiscontinuationAssessmentType"
                        label={t('central.channels.fulfillment.trackDiscontinuationAssessment')}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        initialValue={2}
                        rules={[{ required: true, message: String(t('common.isRequired')) }]}
                    >
                        <Select
                            placeholder={String(t('common.pleaseSelect'))}
                            options={[
                                { value: 1, label: t('central.channels.fulfillment.trackDiscontinuationAssess') },
                                { value: 2, label: t('central.channels.fulfillment.trackDiscontinuationNoAssess') }
                            ]}
                            onChange={(v: number) => {
                                if (v === 2) {
                                    form.setFieldsValue({ trackDiscontinuationAssessmentDays: undefined });
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.trackDiscontinuationAssessmentType !==
                            currentValues.trackDiscontinuationAssessmentType
                        }
                    >
                        {({ getFieldValue }) => {
                            const trackAssessType = getFieldValue('trackDiscontinuationAssessmentType');
                            if (trackAssessType !== 1) {
                                return null;
                            }
                            return (
                                <Form.Item
                                    name="trackDiscontinuationAssessmentDays"
                                    label={t('central.channels.fulfillment.trackDiscontinuationAssessmentDays')}
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                    required
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                const rangeMessage = String(
                                                    t('central.channels.fulfillment.trackDiscontinuationAssessmentDaysRange')
                                                );
                                                const requiredMessage = String(t('common.isRequired'));
                                                if (value === null || value === undefined || value === '') {
                                                    return Promise.reject(new Error(requiredMessage));
                                                }
                                                const num = Number(value);
                                                if (!Number.isInteger(num) || num <= 0 || num >= 100) {
                                                    return Promise.reject(new Error(rangeMessage));
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                    validateTrigger="onBlur"
                                >
                                    <InputNumber
                                        precision={0}
                                        step={1}
                                        controls={false}
                                        className="w-full max-w-md"
                                        addonAfter={t('common.day')}
                                        parser={(displayValue) => {
                                            const parsed = displayValue?.replace(/[^\d]/g, '') ?? '';
                                            return parsed === '' ? '' : Number(parsed);
                                        }}
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </div>
            )
        }
    ];

    return (
        <>
        <EditableDrawer
            title={title}
            onClose={onClose}
            visible={visible}
            width={900}
            onSubmit={readOnly ? undefined : handleSubmit}
            confirmText={t('components.button.confirm') as string}
            cancelText={t('components.button.cancel') as string}
            confirmDisabled={submitting}
            maskClosable={false}
            footer={readOnly ? (
                <div className="flex justify-end">
                    <Button onClick={onClose}>{t('components.button.cancel')}</Button>
                </div>
            ) : undefined}
        >
            <Form
                form={form}
                disabled={readOnly}
                layout="horizontal"
                labelAlign="left"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        // 记录用户访问过的标签页
                        setVisitedTabs(prev => new Set([...prev, key]));
                    }}
                    items={tabItems}
                />
            </Form>
            <Modal
                title={t('central.channels.contact.confirmSendTitle')}
                open={sendKeyModalVisible}
                onCancel={() => {
                    if (sendingApiKey) {
                        return; // 发送中时不允许关闭
                    }
                    setSendKeyModalVisible(false);
                    setConfirmationChars(['', '', '', '']);
                    setConfirmationInputs(['', '', '', '']);
                    setSendKeyDisabled(true);
                    setSendingApiKey(false);
                }}
                footer={[
                    <Button
                        key="send"
                        type="primary"
                        onClick={handleSendApiKey}
                        disabled={sendKeyDisabled || sendingApiKey}
                        loading={sendingApiKey}
                        style={{ marginRight: 8 }}
                    >
                        {t('central.channels.contact.send')}
                    </Button>,
                    <Button
                        key="cancel"
                        onClick={() => {
                            if (sendingApiKey) {
                                return; // 发送中时不允许关闭
                            }
                            setSendKeyModalVisible(false);
                            setConfirmationChars(['', '', '', '']);
                            setConfirmationInputs(['', '', '', '']);
                            setSendKeyDisabled(true);
                            setSendingApiKey(false);
                        }}
                        disabled={sendingApiKey}
                    >
                        {t('central.channels.contact.cancelSend')}
                    </Button>
                ]}
            >
                <div style={{ marginBottom: 16 }}>
                    <p style={{ marginBottom: 8 }}>
                        {t('central.channels.messages.sendApiKeyConfirmMessage', {
                            channelName: channel?.channelName ? `【${channel.channelName}】` : '',
                            channelId: channelId
                        })}
                    </p>
                    <p style={{ marginBottom: 16, color: '#666' }}>
                        {t('central.channels.contact.confirmSendInstruction')}
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
                        {confirmationInputs.map((input, index) => (
                            <Input
                                key={index}
                                value={input}
                                onChange={(e) => handleConfirmationInputChange(index, e.target.value)}
                                onBlur={() => handleConfirmationInputBlur(index)}
                                style={{
                                    width: 60,
                                    height: 60,
                                    textAlign: 'center',
                                    fontSize: 20,
                                    fontWeight: 'bold'
                                }}
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>
                </div>
            </Modal>
            <OnboardingDocumentDownloadModal
                visible={onboardingDownloadModalOpen}
                onClose={() => setOnboardingDownloadModalOpen(false)}
                fileName={onboardingDownloadModalFileName}
            />
        </EditableDrawer>

        {/* CRM: 客户账号管理弹窗 */}
        {channelId && (
            <CustomerAccountModal
                visible={accountModalVisible}
                onClose={() => setAccountModalVisible(false)}
                channelId={channelId}
                channelName={channel?.channelName}
                itContactEmail={completeInfo?.getCompleteChannelInfo?.channelContact?.itContactEmail}
            />
        )}

        {/* CRM: 操作日志查询弹窗 */}
        {channelId && (
            <CustomerLogModal
                visible={logModalVisible}
                onClose={() => setLogModalVisible(false)}
                channelId={channelId}
            />
        )}
        </>
    );
};

export default ChannelFormDrawer;
